import type { QueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';

import { isFinancialUnderlyingSeries } from 'components/Graph/financialUtils';
import type { Category } from 'constants/categories';
import { BECategory, parseBECategoryToCategory } from 'constants/categories';
import type { PERIOD_TYPE } from 'constants/charting';
import { prepositions } from 'constants/prepositions';
import {
    capStrikeKey,
    colorsPaletteKey,
    contractTickerKey,
    countKey,
    floorStrikeKey,
    imageUrlKey,
    separatorTypeKeys,
} from 'constants/rulebookVariables';
import Palette from 'DesignSystem2/Theme/Palette';
import type { TopLevelHue } from 'DesignSystem2/Theme/Palette';
// Type-only, deliberately: a value import would pull `createTickerWatcher` → `transport/wsClients`
// and `authStore` into every importer of this module, including the Server Component graph that
// `queries/marketEntitySeed.ts` was split out to keep hook-free.
import type { LifecycleStatus, QuoteData } from 'lib/stores/tickerQuotesStore';
import type { ColorsPalette } from 'models/colorsPalette';
import { centsFromDollarsString } from 'models/dollars';
import type { Bucket, CollateraReturnType, EventProductMetadata, EventVo } from 'models/event';
import type {
    HistoricalDataPeriodInterval,
    HistoricalMarketCandlestickStats,
    MarketCandlestickVo,
    MarketStatsType,
} from 'models/historical_data';
import type {
    CustomStrike,
    MarketResult,
    MarketStatus,
    MarketVo,
    PriceLevelStructure,
    SettlementSource,
    StrikeType,
} from 'models/market';
import type { FeeType, Frequency, ProductMetadata, SeriesVo } from 'models/series';
import type { HistoricalMarketStats } from 'models/stats';
import { getCachedStructuredTargetById } from 'queries/structuredTargets';
import { linspace, makeMembersCallable } from 'utils/arrays';
import { asCents, lowerFirstLetter } from 'utils/caseing';
import { getBalanceOfPowerColorKey } from 'utils/chartColors';
import {
    calculateStartTs,
    getCandlestickSamplingPointsNumber,
    getLineSamplingPointsNumber,
    periodIntervalToSeconds,
} from 'utils/charting';
import { getColorSchemaFromPalette } from 'utils/colors';
import config from 'utils/config';
import { getEventRelativeUrl, getMinimalSubtitle, MarketForm } from 'utils/events';
import type { EffectiveFeeParams } from 'utils/fees';
import {
    isFeeWaiverExpired,
    priceToAmericanOdds,
    resolveEffectiveFeeParams,
    resolveSeriesFeeMultiplier,
} from 'utils/fees';
import { getEventImageUrl, toCdnImageUrl } from 'utils/images';
import type { EventPricingInfo } from 'utils/marketPricing';
import { normalizedLastYesPriceCents, resolveInverseTicker } from 'utils/marketPricing';
import { applyPrioritizedStrikes, compareByPriceDescThenTicker, isTieMarket } from 'utils/markets';
import { max, min } from 'utils/math';
import type { NumericTarget } from 'utils/numericTarget';
import { computeNumericTarget } from 'utils/numericTarget';
import { isValidPrice } from 'utils/price';
import { getSafeProbability } from 'utils/probability';
import { escapeRegExp } from 'utils/regex';
import { getSeriesRelativeUrl, luigiMangioneSeriesTickers } from 'utils/series';
import { getIsClosingSoon } from 'utils/time';
import { MAX_HIGHLIGHTED_MARKETS } from 'utils/widgets';

/**
 * Plain-data mirror of `stores/domain/BinaryMarket`'s most-read fields (price,
 * status, result). Financial-core parity with the original getters is enforced by
 * `marketEntityTransform.vitest.ts` — extend both together.
 *
 * Cross-market price normalization (`marketNormalizedLastYesPrice`/`marketSafeProbability`/
 * `marketInverseTicker`), strike range logic (`marketIsOnRange`/`marketIsOnTheBestRange`/
 * `marketNumericTarget`), forecast/candlestick history, and payout are NOT stored as fields
 * on this interface — they're derived by standalone functions elsewhere (further down this
 * file, plus `marketPayout.ts`) rather than being part of the plain-data shape itself.
 */
export interface MarketEntity {
    id: string;
    ticker: string;
    /**
     * Owning event's ticker — stamped from the enclosing event VO when hydrated via an event
     * fetch (`eventMarketVoToMarketEntity`), else read off the market VO itself
     * (`event_ticker`/`ranged_group_ticker`). Optional because not every hydration path knows
     * it; consumers must fall back (e.g. `RulesSection` falls back to the page's event ticker).
     */
    eventTicker?: string;
    title: string;
    subtitle: string;
    /** Mirrors `BinaryMarket.rawSubTitle` (`vo.sub_title`, no `yes_sub_title` fallback). */
    rawSubTitle: string;
    /** Mirrors `BinaryMarket.yesSubtitle` (`vo.yes_sub_title`, no `sub_title` fallback — unlike `subtitle`). */
    yesSubtitle: string;
    /**
     * Provenance of `subtitle`/`yesSubtitle`: `true` only when they came from a market nested in an
     * `/v1/events...` payload (`eventMarketVoToMarketEntity`), which is the ONLY source that applies
     * the partner-league official-name override ("Baltimore Ravens" vs the market-level endpoints'
     * broker-safe "Baltimore"). `queries/markets.ts`' `preservingEventSourcedFields` reads this to
     * decide whether a market-level re-hydration may overwrite the stored names: an event-sourced
     * name is kept, a market-sourced one is replaced so genuine renames still land. This flag
     * covers the names only — the helper's other preserved field (`expectedExpirationDate`) needs
     * no provenance because market-level payloads never carry it at all.
     */
    namesFromEvent?: boolean;

    openDate: string;
    closeDate: string;
    /** Mirrors `BinaryMarket.listDate` (`vo.list_date`) — when the strike became tradeable. */
    listDate: string;
    expirationDate: string;
    expectedExpirationDate: string;
    settlementTimerSeconds: number;
    settlementValueCents: number;
    strikeType: StrikeType;
    rulebookVariables: Record<string, string>;
    colors?: ColorsPalette;
    customStrike: CustomStrike | undefined;
    /** Top-level numeric strike fields from the API; feeds `marketNumericTarget`. Missing on pre-Feb-2025 series. */
    floorStrike: number | undefined;
    capStrike: number | undefined;
    /** Feeds `marketParsedFeeWaiverExpirationDate`/`marketHasFeeWaiverExpired`. */
    feeWaiverExpirationDate: string | null;

    status: MarketStatus | undefined;
    result: MarketResult;
    determinationTs: number | undefined;
    settledTs: number | undefined;
    /** Mirrors `BinaryMarket.expirationValue` (`vo.expiration_value`, a raw passthrough). */
    expirationValue: string;

    yesBid: number;
    yesAsk: number;
    noBid: number;
    noAsk: number;
    yesPriceInCents: number | null;
    noPriceInCents: number | null;
    lastPrice: number;
    /** Mirrors `BinaryMarket.previousDayPrice` (`vo.previous_day_price`, falling back to `lastPrice`). */
    previousDayPrice: number;
    /** Mirrors `BinaryMarket.previousHourPrice` (`vo.previous_hour_price`, falling back to `lastPrice`). REST-only. */
    previousHourPrice: number;
    /** Mirrors `BinaryMarket.previousWeekPrice` (`vo.previous_week_price`, falling back to `lastPrice`). REST-only. */
    previousWeekPrice: number;
    /** Mirrors `BinaryMarket.fractionalTradingEnabled` (`vo.fractional_trading_enabled`). REST-only. */
    fractionalTradingEnabled: boolean;
    /** Mirrors `BinaryMarket.priceLevelStructure` (`vo.price_level_structure`, defaulting to `'linear_cent'`). */
    priceLevelStructure: PriceLevelStructure;
    /**
     * Mirrors `BinaryMarket.previousPeriodPrice` (`vo.prev_period_price_dollars`, falling back to
     * `lastPrice`). REST-only — confirmed `BinaryMarket` never updates this from the WS ticker
     * payload, so no `applyTickerToMarketEntity` counterpart is needed.
     */
    previousPeriodPrice: number;
    volume: number;
    openInterest: number;
    recentVolume: number;
}

/** Plain-data mirror of `stores/domain/Event`'s identity fields; status/volume are derived — see the `event*` functions below. */
export interface EventEntity {
    ticker: string;
    seriesTicker: string;
    title: string;
    /** Mirrors `Event.subTitle` (`vo.sub_title.trim()`). */
    subTitle: string;
    /** Mirrors `Event.targetDatetime` (`vo.target_datetime`). */
    targetDatetime: string;
    maxCloseTs: string | undefined;
    marketTickers: string[];
    /** `product_metadata.custom_image_url` — the backend-controlled event image override. The displayed
     * image is `eventImageUrl(event, series)`: it needs the series' versioned artwork URL, which events
     * don't carry, so there is deliberately no precomputed `imageUrl` on the entity. */
    customImageUrl: string | undefined;
    feeTypeOverride: FeeType | undefined;
    feeMultiplierOverride: number | undefined;
    /** At most one sibling market resolves Yes — feeds cross-market price normalization, see `marketNormalizedLastYesPrice`. */
    mutuallyExclusive: boolean;
    productMetadata: EventProductMetadata;
    productMetadataDerived: Record<string, string>;
    settlementSources: SettlementSource[];
    collateralReturnType: CollateraReturnType | undefined;
    /** Mirrors `Event.rawUnderlying` (`vo.underlying`, before rulebook-variable substitution). */
    rawUnderlying: string;
    /** Mirrors `Event.rawSettleDetails` (`vo.settle_details`, before rulebook-variable substitution). */
    rawSettleDetails: string;
}

const lifecycleSecondsToIso = (seconds: number): string | undefined => {
    return DateTime.fromSeconds(seconds, { zone: 'utc' }).toISO() ?? undefined;
};

/* -------------------------------------------------------------------------- */
/*                          MARKET: derived predicates                        */
/* -------------------------------------------------------------------------- */

export const isMarketOpen = (market: MarketEntity): boolean => market.status === 'active';

export const isMarketPaused = (market: MarketEntity): boolean => market.status === 'inactive';

export const isMarketSettled = (market: MarketEntity): boolean =>
    market.status === 'settled' || market.status === 'finalized' || !!market.settledTs;

export const isMarketDetermined = (market: MarketEntity): boolean =>
    market.status === 'determined' ||
    market.status === 'disputed' ||
    market.status === 'amended' ||
    isMarketSettled(market) ||
    !!market.result ||
    !!market.determinationTs;

/** Mirrors `BinaryMarket.isClosed`; `now` defaults to the call-time clock (no reactive timer — recompute on your own cadence). */
export const isMarketClosed = (market: MarketEntity, now: DateTime = DateTime.now()): boolean =>
    market.status === 'closed' ||
    isMarketPaused(market) ||
    isMarketDetermined(market) ||
    (!!market.closeDate && DateTime.fromISO(market.closeDate) < now);

export const isMarketPendingPayout = (market: MarketEntity): boolean =>
    isMarketDetermined(market) && !isMarketSettled(market);

/**
 * Mirrors `BinaryMarket.isHidden`. `marketsToRemove` is `useFeatureFlagStore.getState().marketsToRemove` —
 * pass it in rather than reading the store here, since this is a plain function, not a hook.
 */
export const isMarketHidden = (
    market: MarketEntity,
    event: Pick<EventEntity, 'seriesTicker'>,
    marketsToRemove: string[],
): boolean => marketsToRemove.includes(market.ticker) || luigiMangioneSeriesTickers.includes(event.seriesTicker);

export const marketResultText = (market: MarketEntity): string => {
    if (!market.result) return 'Pending';
    if (market.result === 'yes') return 'Yes';
    if (market.result === 'no') return 'No';
    if (market.result === 'scalar' && market.settlementValueCents) return `${market.settlementValueCents}%`;
    return '';
};

export const marketResultColor = (market: MarketEntity): string => {
    if (!market.result) return Palette.Text.x10;
    if (market.result === 'yes') return Palette.Yes.x10;
    if (market.result === 'no') return Palette.No.x10;
    return Palette.Text.x10;
};

/** Mirrors `BinaryMarket.resultIsYes`. */
export const marketResultIsYes = (market: MarketEntity): boolean => market.result === 'yes';
/** Mirrors `BinaryMarket.resultIsNo`. */
export const marketResultIsNo = (market: MarketEntity): boolean => market.result === 'no';
/** Mirrors `BinaryMarket.resultIsScalar`. */
export const marketResultIsScalar = (market: MarketEntity): boolean => market.result === 'scalar';

/** Mirrors `BinaryMarket.contractDecimals`. */
export const marketContractDecimals = (market: MarketEntity): number => (market.fractionalTradingEnabled ? 2 : 0);

/** Mirrors `BinaryMarket.relativeUrl`. */
export const marketRelativeUrl = (
    market: MarketEntity,
    event: Pick<EventEntity, 'seriesTicker' | 'ticker'>,
    seriesTitle: string | undefined,
): string => getEventRelativeUrl(event.seriesTicker, seriesTitle, event.ticker, market.ticker);

/**
 * Mirrors `BinaryMarket.yesSellPrice`/`.noSellPrice` (the price you're filled at selling Yes/No
 * contracts right now). Algebraically these reduce to `yesBid`/`100 - yesAsk` — `yesSellPrice =
 * 100 - noAsk = 100 - (100 - yesBid) = yesBid` — kept as named functions for parity with the
 * original API rather than inlining the identity at call sites.
 */
export const marketYesSellPrice = (market: MarketEntity): number => market.yesBid;
export const marketNoSellPrice = (market: MarketEntity): number => 100 - market.yesAsk;

/** Mirrors `BinaryMarket.{yes,no}{,Sell}PriceInCentsAsString` — display-formatted current buy/sell prices. */
export const marketYesPriceInCentsAsString = (market: MarketEntity): string =>
    market.yesAsk === 100 ? '--¢' : asCents(market.yesAsk);
export const marketNoPriceInCentsAsString = (market: MarketEntity): string =>
    market.noAsk === 100 ? '--¢' : asCents(market.noAsk);
export const marketYesSellPriceInCentsAsString = (market: MarketEntity): string => {
    const price = marketYesSellPrice(market);
    return price === 0 ? '--¢' : asCents(price);
};
export const marketNoSellPriceInCentsAsString = (market: MarketEntity): string => {
    const price = marketNoSellPrice(market);
    return price === 0 ? '--¢' : asCents(price);
};

/** Mirrors `BinaryMarket.tip` (`rawSubTitle`'s `::`-delimited second segment). */
export const marketTip = (market: MarketEntity): string | undefined => market.rawSubTitle.split('::')[1]?.trim();

/** Mirrors `BinaryMarket.tipWithSideContext` — suppresses an "or another"-style tip on the Yes side. */
export const marketTipWithSideContext = (market: MarketEntity, isYes: boolean): string => {
    const tip = marketTip(market);
    if (!tip) return '';
    if (tip.toLowerCase().includes('or another') && isYes) return '';
    return tip;
};

/**
 * Mirrors `BinaryMarket.detachNumberFromTitle` — splits a title into the text before/after its
 * first standalone number, for sports/financial-index tiles that render the number in a distinct
 * style. `eventSubTitle`/`seriesTitle` are the same market→event→series subtitle fallback chain
 * used elsewhere (no `parentEvent`/`.series` back-reference exists on `MarketEntity`, so callers
 * pass what they already have).
 */
export const marketDetachNumberFromTitle = (
    market: MarketEntity,
    eventSubTitle: string,
    seriesTitle: string | undefined,
): { beforeNumber: string; number: string | undefined; afterNumber: string | undefined } => {
    const title = market.subtitle || eventSubTitle || seriesTitle || '';
    const splitTitle = title.split(' ');
    const indexOfNumber = splitTitle.findIndex((part) => !isNaN(Number(part.replace('+', '').replace('-', ''))));
    const beforeNumber = splitTitle.slice(0, indexOfNumber).join(' ');
    const number = indexOfNumber < splitTitle.length ? splitTitle[indexOfNumber]?.trim() : undefined;
    const afterNumber = indexOfNumber < splitTitle.length ? splitTitle.slice(indexOfNumber + 1).join(' ') : undefined;
    return { beforeNumber, number, afterNumber };
};

/* -------------------------------------------------------------------------- */
/*                          MARKET: REST + WS hydration                       */
/* -------------------------------------------------------------------------- */

export const marketVoToMarketEntity = (vo: MarketVo): MarketEntity => {
    const yesBid = centsFromDollarsString(vo.yes_bid_dollars, vo.yes_bid ?? 0);
    const yesAsk = centsFromDollarsString(vo.yes_ask_dollars, vo.yes_ask ?? 0);
    const lastPrice = centsFromDollarsString(vo.last_price_dollars, vo.last_price ?? 0);
    const noBid = 100 - yesAsk;
    const noAsk = 100 - yesBid;
    const previousDayPrice = isValidPrice(vo.previous_day_price) ? (vo.previous_day_price as number) : lastPrice;
    const previousHourPrice = isValidPrice(vo.previous_hour_price) ? (vo.previous_hour_price as number) : lastPrice;
    const previousWeekPrice = isValidPrice(vo.previous_week_price) ? (vo.previous_week_price as number) : lastPrice;
    const previousPeriodPriceCandidate = centsFromDollarsString(vo.prev_period_price_dollars, NaN);
    const previousPeriodPrice = isValidPrice(previousPeriodPriceCandidate) ? previousPeriodPriceCandidate : lastPrice;

    return {
        id: vo.id,
        ticker: vo.ticker_name,
        eventTicker: vo.event_ticker ?? vo.ranged_group_ticker,
        title: (vo.title ?? '').split('::')[0]?.trim().replace(/\*\*/g, '') ?? '',
        subtitle: vo.yes_sub_title || vo.sub_title || '',
        rawSubTitle: vo.sub_title,
        yesSubtitle: vo.yes_sub_title || '',

        openDate: vo.open_date,
        closeDate: vo.close_date,
        listDate: vo.list_date,
        expirationDate: vo.expiration_date,
        expectedExpirationDate: vo.expected_expiration_date,
        settlementTimerSeconds: vo.settlement_timer_seconds,
        // The focused market endpoint omits settlement_value_centicents even after a
        // binary market is finalized. Derive the canonical binary Yes payout from the
        // result so its anonymous 4s poll cannot replace the event endpoint's 100c value
        // with zero (No already correctly falls back to zero).
        settlementValueCents: (vo.settlement_value_centicents ?? (vo.result === 'yes' ? 10000 : 0)) / 100,
        strikeType: vo.strike_type ?? '',
        rulebookVariables: vo.rulebook_variables ?? {},
        colors: vo.colors,
        customStrike: vo.custom_strike,
        floorStrike: vo.floor_strike,
        capStrike: vo.cap_strike,
        feeWaiverExpirationDate: vo.fee_waiver_expiration_date ?? null,

        expirationValue: vo.expiration_value,

        status: vo.status,
        result: vo.result ?? '',
        determinationTs: undefined,
        settledTs: undefined,

        yesBid,
        yesAsk,
        noBid,
        noAsk,
        yesPriceInCents: yesAsk === 100 ? null : yesAsk,
        noPriceInCents: noAsk === 100 ? null : noAsk,
        lastPrice,
        previousDayPrice,
        previousHourPrice,
        previousWeekPrice,
        previousPeriodPrice,
        fractionalTradingEnabled: vo.fractional_trading_enabled ?? false,
        priceLevelStructure: vo.price_level_structure ?? 'linear_cent',
        volume: vo.volume / 2,
        openInterest: vo.open_interest / 2,
        recentVolume: vo.dollar_recent_volume,
    };
};

/**
 * `marketVoToMarketEntity` for markets nested inside an event VO — stamps the enclosing
 * event's ticker, which the nested v1 payload doesn't reliably carry on the market itself.
 * `RulesSection` relies on this to resolve a market's own event when a game page inlines
 * prop markets from a sibling event. Also marks the display names event-sourced (see
 * `namesFromEvent`) so a later market-level re-hydration doesn't drop the official-name override.
 */
export const eventMarketVoToMarketEntity = (vo: MarketVo, eventTicker: string): MarketEntity => ({
    ...marketVoToMarketEntity(vo),
    eventTicker,
    namesFromEvent: true,
});

/**
 * A `tickerQuotesStore` entry for one market. Structurally compatible with
 * `hooks/useLiveMarketQuotes`' `LiveMarketData`, declared locally so this module stays free of a
 * dependency on the store (see the type-only import note above).
 */
export interface LiveMarketOverlay {
    quote?: QuoteData;
    status?: LifecycleStatus;
}

/**
 * Overlays a live `tickerQuotesStore` entry onto a REST-hydrated `MarketEntity`, for surfaces that
 * subscribe for themselves via `useLiveMarketQuotes`. `marketEntitiesStore` is REST-owned, so this
 * is the only path from live WS data to an entity-shaped view model.
 *
 * Field coverage matches what the store's own `ticker`/`market_lifecycle_v2` handlers used to patch,
 * with one gap: `LifecycleStatus` carries no `open_ts`, so an open-time amendment lands only on the
 * next REST poll. It also fills `settlementValueCents`, which the old lifecycle handler never set.
 *
 * Returns the IDENTICAL `market` reference when nothing changed. `BinaryMarketTile`'s
 * `arePropsEqual` and `MarketTiles`' `sameVisibleMarkets` both reference-compare markets, so
 * allocating a fresh object per tick would defeat the point of subscribing per card.
 */
export const applyLiveMarketDataToEntity = (
    market: MarketEntity,
    live: LiveMarketOverlay | undefined,
): MarketEntity => {
    const quote = live?.quote;
    const status = live?.status;
    if (!quote && !status) return market;

    const yesBid = quote?.yesBid ?? market.yesBid;
    const yesAsk = quote?.yesAsk ?? market.yesAsk;
    // Taken from the quote rather than recomputed as `100 - yesAsk`: the store derives these with
    // `Decimal`, so deci-cent markets keep exact values instead of float noise (100 - 99.9).
    const noBid = quote?.noBid ?? market.noBid;
    const noAsk = quote?.noAsk ?? market.noAsk;
    const volume = quote?.volume ?? market.volume;
    const openInterest = quote?.openInterest ?? market.openInterest;
    // Only accept a real traded value (1-99c) — around determination the ticker can carry a
    // degenerate 0 (empty-book best-bid fallback) that would drag chart endpoints to 0.
    const lastPrice = quote && quote.price > 0 && quote.price < 100 ? quote.price : market.lastPrice;

    const determinationTs = status?.determinationTs ?? market.determinationTs;
    const settledTs = status?.settledTs ?? market.settledTs;
    const result = (status?.result as MarketResult) || market.result;
    const settlementValueCents = status?.settlementValueCents ?? market.settlementValueCents;
    const closeDate =
        (status?.closedTs != null ? lifecycleSecondsToIso(status.closedTs) : undefined) ?? market.closeDate;

    // Same precedence as the deleted `applyLifecycleToMarketEntity`, branching off the MERGED
    // values so a frame carrying only `isDeactivated` can't regress a determined/settled market
    // back to active.
    let nextStatus = market.status;
    if (settledTs !== undefined) {
        nextStatus = 'settled';
    } else if (determinationTs !== undefined || result) {
        nextStatus = 'determined';
    } else if (status && status.isDeactivated !== null) {
        nextStatus = status.isDeactivated ? 'inactive' : 'active';
    }

    if (
        yesBid === market.yesBid &&
        yesAsk === market.yesAsk &&
        noBid === market.noBid &&
        noAsk === market.noAsk &&
        lastPrice === market.lastPrice &&
        volume === market.volume &&
        openInterest === market.openInterest &&
        determinationTs === market.determinationTs &&
        settledTs === market.settledTs &&
        result === market.result &&
        settlementValueCents === market.settlementValueCents &&
        closeDate === market.closeDate &&
        nextStatus === market.status
    ) {
        return market;
    }

    return {
        ...market,
        yesBid,
        yesAsk,
        noBid,
        noAsk,
        yesPriceInCents: yesAsk === 100 ? null : yesAsk,
        noPriceInCents: noAsk === 100 ? null : noAsk,
        lastPrice,
        volume,
        openInterest,
        determinationTs,
        settledTs,
        result,
        settlementValueCents,
        closeDate,
        status: nextStatus,
    };
};

/**
 * Array form of `applyLiveMarketDataToEntity`, preserving the ARRAY reference when every element is
 * unchanged — callers pass the result straight into props and `useMemo` deps.
 */
export const applyLiveMarketDataToEntities = (
    markets: MarketEntity[],
    liveData: Record<string, LiveMarketOverlay>,
): MarketEntity[] => {
    // Copies lazily rather than mapping first and discarding the result: callers run this on every
    // render (it's cheap enough not to memoize), and the no-change case is by far the common one, so
    // an unconditional `map` would allocate a throwaway array per render per consumer.
    let next: MarketEntity[] | undefined;
    for (let i = 0; i < markets.length; i++) {
        const overlaid = applyLiveMarketDataToEntity(markets[i], liveData[markets[i].ticker]);
        if (overlaid === markets[i]) continue;
        next ??= markets.slice();
        next[i] = overlaid;
    }
    return next ?? markets;
};

/* -------------------------------------------------------------------------- */
/*                          EVENT: REST hydration + aggregates                */
/* -------------------------------------------------------------------------- */

export const eventVoToEventEntity = (vo: EventVo): EventEntity => {
    const customImageUrl =
        typeof vo.product_metadata?.custom_image_url === 'string' ? vo.product_metadata.custom_image_url : undefined;

    return {
        ticker: vo.ticker,
        seriesTicker: vo.series_ticker,
        title: vo.title,
        subTitle: vo.sub_title.trim(),
        targetDatetime: vo.target_datetime,
        maxCloseTs: vo.max_close_ts,
        marketTickers: vo.markets.map((m) => m.ticker_name),
        customImageUrl,
        feeTypeOverride: vo.fee_type_override,
        feeMultiplierOverride: vo.fee_multiplier_override,
        mutuallyExclusive: vo.mutually_exclusive,
        productMetadata: vo.product_metadata || {},
        productMetadataDerived: vo.product_metadata_derived || {},
        settlementSources: vo.settlement_sources,
        collateralReturnType: vo.collateral_return_type,
        rawUnderlying: vo.underlying,
        rawSettleDetails: vo.settle_details,
    };
};

/**
 * Display image for an event: the event's `custom_image_url` override, else the series artwork.
 * Pass the series when you have it — its `imageUrl` is the backend's versioned URL, so replaced
 * artwork busts browser/Vercel caches. Without it the ticker-derived (unversioned) URL is used.
 */
export const eventImageUrl = (
    event: Pick<EventEntity, 'seriesTicker' | 'customImageUrl'>,
    series?: Pick<SeriesEntity, 'imageUrl'>,
): string =>
    getEventImageUrl({
        seriesTicker: event.seriesTicker,
        customImageUrl: event.customImageUrl,
        seriesImageUrl: series?.imageUrl,
    });

/** Mirrors `Event.isDirectional`. */
export const eventIsDirectional = (event: Pick<EventEntity, 'collateralReturnType'>): boolean =>
    event.collateralReturnType === 'DIRECNET';

/**
 * Mirrors `Event.effectiveFeeParams`. `series` is looked up by the caller (e.g.
 * `marketEntitiesStore.getState().series[event.seriesTicker]`) rather than linked
 * on the entity itself — this store is normalized, with relations resolved by
 * key lookup rather than object references.
 */
export const eventEffectiveFeeParams = (event: EventEntity, series: SeriesEntity | undefined): EffectiveFeeParams =>
    resolveEffectiveFeeParams({
        series: series && { ticker: series.ticker, fee_type: series.feeType, fee_multiplier: series.feeMultiplier },
        overrides: { fee_type_override: event.feeTypeOverride, fee_multiplier_override: event.feeMultiplierOverride },
    });

/** Mirrors `Event.isNoFee`. */
export const eventIsNoFee = (event: EventEntity, series: SeriesEntity | undefined): boolean =>
    eventEffectiveFeeParams(event, series).fee_multiplier === 0;

const STATUS_PRIORITIES: MarketStatus[] = [
    'active',
    'initialized',
    'closed',
    'determined',
    'amended',
    'disputed',
    'settled',
    'finalized',
    'inactive',
    'invalid',
];

/** Mirrors `Event.mostActiveStatus`. `markets` must be this event's own markets, already resolved from `marketTickers`. */
export const eventMostActiveStatus = (markets: MarketEntity[]): MarketStatus | 'settled' | null => {
    if (markets.length === 0) return null;
    const statuses = new Set(markets.map((m) => m.status));
    for (const status of STATUS_PRIORITIES) {
        if (statuses.has(status)) return status;
    }
    return 'settled';
};

export const eventVolume = (markets: MarketEntity[]): number => markets.reduce((sum, m) => sum + m.volume, 0);

export const eventOpenInterest = (markets: MarketEntity[]): number =>
    markets.reduce((sum, m) => sum + m.openInterest, 0);

export const eventHasOpenMarkets = (markets: MarketEntity[]): boolean => markets.some(isMarketOpen);

export const eventIsPaused = (markets: MarketEntity[]): boolean => markets.some(isMarketPaused);

/** Mirrors `Event.areAllMarketsPaused`. */
export const eventAreAllMarketsPaused = (markets: MarketEntity[]): boolean =>
    markets.length > 0 && markets.every(isMarketPaused);

export const eventIsSettled = (markets: MarketEntity[]): boolean => {
    const status = eventMostActiveStatus(markets);
    return status === 'settled' || status === 'finalized';
};

export const eventIsDetermined = (markets: MarketEntity[]): boolean => {
    if (eventIsSettled(markets)) return true;
    const status = eventMostActiveStatus(markets);
    return status === 'determined' || status === 'disputed';
};

/** True while every market hasn't opened yet — mirrors `Event.isPendingEvent`. */
export const eventIsPending = (markets: MarketEntity[], now: DateTime = DateTime.now()): boolean =>
    markets.length > 0 && markets.every((m) => DateTime.fromISO(m.openDate) > now);

/** Mirrors `Event.isClosed`, minus the `!loaded` short-circuit (an `EventEntity` only exists once loaded). */
export const eventIsClosed = (event: EventEntity, markets: MarketEntity[], now: DateTime = DateTime.now()): boolean => {
    const isTimeClosed = !!event.maxCloseTs && DateTime.fromISO(event.maxCloseTs) < now;
    if (isTimeClosed || eventMostActiveStatus(markets) === 'closed' || eventIsDetermined(markets)) return true;

    return !eventHasOpenMarkets(markets) && !eventIsPending(markets, now);
};

/** Mirrors `Event.isSportsGameEvent`. */
export const eventIsSportsGameEvent = (event: Pick<EventEntity, 'ticker' | 'productMetadata'>): boolean =>
    event.ticker.includes('GAME') && !!event.productMetadata?.competition;

/**
 * Mirrors `Event.visibleMarkets`. `marketsToRemove` is
 * `useFeatureFlagStore.getState().marketsToRemove` — pass it in rather than reading
 * the store here, since this is a plain function, not a hook.
 */
export const eventVisibleMarkets = (
    markets: MarketEntity[],
    event: Pick<EventEntity, 'seriesTicker'>,
    marketsToRemove: string[],
): MarketEntity[] => markets.filter((m) => !isMarketHidden(m, event, marketsToRemove));

/** Mirrors `Event.marketsHaveImplicitOrdering`. Pass `eventVisibleMarkets(...)`'s output. */
export const eventMarketsHaveImplicitOrdering = (visibleMarkets: MarketEntity[]): boolean =>
    visibleMarkets.every((m) => /\d|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/.test(m.subtitle));

/** Mirrors `Event.allMarketsClosed`. */
export const eventAllMarketsClosed = (markets: MarketEntity[], now: DateTime = DateTime.now()): boolean =>
    markets.every((m) => isMarketClosed(m, now));

/** Mirrors `Event.determinedMarketToYes`. */
export const eventDeterminedMarketToYes = (markets: MarketEntity[]): MarketEntity | null => {
    const determinedMarketsToYes = markets.filter((m) => m.result === 'yes');
    return determinedMarketsToYes.length === 1 ? determinedMarketsToYes[0] : null;
};

/**
 * Mirrors `Event.markets` — the sorted/filtered set the UI actually renders: hides
 * removed/Luigi-Mangione markets, prioritizes the more likely market when unordered,
 * prioritizes undetermined over determined, and brings a lone Yes winner to the top
 * once every market is closed and the event isn't a numeric ladder. Compares the
 * winner by `.id` rather than object identity (unlike the original getter) since entities
 * here are recreated on every WS/REST update rather than mutated in place.
 */
export const eventMarkets = (
    rawMarkets: MarketEntity[],
    event: Pick<EventEntity, 'seriesTicker'>,
    marketsToRemove: string[],
    now: DateTime = DateTime.now(),
): MarketEntity[] => {
    const visibleMarkets = eventVisibleMarkets(rawMarkets, event, marketsToRemove);
    const hasImplicitOrdering = eventMarketsHaveImplicitOrdering(visibleMarkets);

    const sortedMarkets = visibleMarkets
        .slice()
        .sort((a, b) => (hasImplicitOrdering ? 0 : compareByPriceDescThenTicker(a, b)))
        .sort((a, b) => (isMarketDetermined(a) === isMarketDetermined(b) ? 0 : isMarketDetermined(a) ? 1 : -1));

    const determinedMarketToYes = eventDeterminedMarketToYes(rawMarkets);
    // `visibleMarkets`, not `rawMarkets` — mirrors the deleted `Event.isParsableNumeric`
    // getter, which always self-filtered hidden markets before the numeric-ladder check
    // regardless of what its caller passed. Passing the raw (unfiltered) set here would let
    // one admin-hidden market with a mismatched numeric target wrongly flip a real numeric
    // ladder's `isParsableNumeric` to false, breaking the "bring lone Yes winner to top" logic
    // below once every visible market has settled.
    const shouldBringWinnerToTop =
        determinedMarketToYes && eventAllMarketsClosed(rawMarkets, now) && !eventIsParsableNumeric(visibleMarkets);
    if (shouldBringWinnerToTop) {
        sortedMarkets.sort((a, b) =>
            a.id === determinedMarketToYes.id ? -1 : b.id === determinedMarketToYes.id ? 1 : 0,
        );
    }

    return sortedMarkets;
};

/* -------------------------------------------------------------------------- */
/*                          SERIES: REST hydration + derived predicates       */
/* -------------------------------------------------------------------------- */

/**
 * Plain-data mirror of `stores/domain/Series`'s REST-static fields.
 *
 * Deliberately NOT covered: `isBookmarked` (cross-store — reads `traderStore.user`,
 * not series data; consumers should read `traderStore.user.favoritedSeriesList.includes(ticker)`
 * directly, same as `WatchlistButton`/`MarketTileContainer`), `events` (derived
 * from the whole `MarketStore.allEvents` universe — cross-cutting, not a per-series field),
 * and `isDailyTemperature` (trivial to add later, just not needed by any migrated consumer yet).
 */
export interface SeriesEntity {
    ticker: string;
    title: string;
    frequency: Frequency;
    firstOpenDate: string | undefined;
    category: BECategory | undefined;
    tags: string[];
    keywords: string[];
    contractUrl: string;
    contractTicker?: string;
    /** `SeriesVo.image_url`: the public artwork URL, versioned (`?v=`) once re-uploaded. Feed it to
     * `getSeriesImageUrl` / `eventImageUrl` rather than deriving the URL from the ticker. */
    imageUrl: string | undefined;
    productMetadata: ProductMetadata | undefined;
    additionalProhibitions: string[] | undefined;
    seriesVolume: number;
    forecastFormattingRule?: string;
    feeType: FeeType;
    feeMultiplier: number;
}

const asNonEmptyString = (value: unknown): string | undefined =>
    typeof value === 'string' && value ? value : undefined;

export const seriesVoToSeriesEntity = (vo: SeriesVo): SeriesEntity => ({
    ticker: vo.ticker,
    title: vo.title,
    frequency: vo.frequency,
    firstOpenDate: vo.first_open_date,
    category: vo.category,
    tags: vo.tags ?? [],
    keywords: vo.keywords,
    contractUrl: vo.contract_url,
    contractTicker: vo.contract_ticker,
    // Endpoints that don't resolve `image_url` still carry the stored value in product_metadata.
    imageUrl: asNonEmptyString(vo.image_url) ?? asNonEmptyString(vo.product_metadata?.image_url),
    productMetadata: vo.product_metadata,
    additionalProhibitions: vo.additional_prohibitions,
    seriesVolume: vo.series_volume,
    forecastFormattingRule: asNonEmptyString(vo.forecast_formatting_rule),
    feeType: vo.fee_type,
    feeMultiplier: resolveSeriesFeeMultiplier(vo.ticker, vo.fee_multiplier) ?? vo.fee_multiplier,
});

/** Mirrors `Series.parsedCategory`. */
export const seriesParsedCategory = (series: Pick<SeriesEntity, 'category'>): Category =>
    parseBECategoryToCategory(series.category);

/** Mirrors `Series.relativeUrl`. */
export const seriesRelativeUrl = (series: SeriesEntity): string => getSeriesRelativeUrl(series.ticker, series.title);

/** Mirrors `Event.relativeUrl`. */
export const eventRelativeUrl = (
    event: Pick<EventEntity, 'ticker' | 'seriesTicker'>,
    series: Pick<SeriesEntity, 'title'> | undefined,
): string => getEventRelativeUrl(event.seriesTicker, series?.title, event.ticker);

/** Mirrors `Event.absoluteUrl` (via `Event.relativeUrl`). */
export const eventAbsoluteUrl = (
    event: Pick<EventEntity, 'ticker' | 'seriesTicker'>,
    series: Pick<SeriesEntity, 'title'> | undefined,
): string => `${config.baseUrl}${eventRelativeUrl(event, series)}`;

/** Mirrors `Event.absoluteReferralUrl`. */
export const eventAbsoluteReferralUrl = (series: SeriesEntity | undefined, utmSource?: string): string => {
    const relativeUrl = series ? seriesRelativeUrl(series) : undefined;
    if (!utmSource) return `${config.baseUrl}${relativeUrl}`;
    return `${config.baseUrl}${relativeUrl}?utm_source=${utmSource}`;
};

/**
 * Mirrors `Series.isSports`. Explicitly false for a missing series or an unset `category` —
 * not merely because `parseBECategoryToCategory(undefined)` happens to default to `'general'`,
 * which callers shouldn't have to rely on to get "no series info" right.
 */
export const isSeriesSports = (series: Pick<SeriesEntity, 'category'> | undefined): boolean =>
    series?.category !== undefined && seriesParsedCategory(series) === 'sports';

/** Mirrors `Series.isElection`. `subcategories` is typed as always-present but some
 * series resolve with a `product_metadata` that omits it entirely — guard before indexing. */
export const isSeriesElection = (series: SeriesEntity): boolean =>
    series.productMetadata?.subcategories?.['Politics']?.includes('US Elections') ?? false;

const DAILY_TEMPERATURE_TICKER_PREFIXES = ['KXHIGH', 'KXLOW'];

/** Mirrors `Series.isDailyTemperature`. */
export const isSeriesDailyTemperature = (series: SeriesEntity): boolean =>
    DAILY_TEMPERATURE_TICKER_PREFIXES.some((prefix) => series.ticker.startsWith(prefix)) &&
    series.category === BECategory.CLIMATE;

/* -------------------------------------------------------------------------- */
/*                MARKET: cross-market price normalization                    */
/* -------------------------------------------------------------------------- */

/** Mirrors `BinaryMarket.spread`. */
export const marketSpread = (market: MarketEntity): number => Math.abs(market.yesBid - market.yesAsk);

/** Mirrors `BinaryMarket.midSpread`. */
export const marketMidSpread = (market: MarketEntity): number => Math.floor((market.yesBid + market.yesAsk) / 2);

/** Mirrors `BinaryMarket.lastValidYesPrice`. */
export const marketLastValidYesPrice = (market: MarketEntity): number => {
    const SPREAD_LIMIT = 30;
    const defaultPrice = marketSpread(market) < SPREAD_LIMIT ? marketMidSpread(market) : 50;
    return 0 < market.lastPrice && market.lastPrice < 100 ? market.lastPrice : defaultPrice;
};

/**
 * Mirrors `BinaryMarket.pricingEvent` (private). `eventMarkets` must be `event`'s own
 * markets, already resolved from `EventEntity.marketTickers`. Deliberately uses a raw
 * `series?.category === 'Sports'` string comparison, not `isSeriesSports`, to match the
 * original getter exactly.
 */
export const buildEventPricingInfo = (
    eventMarkets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    series: Pick<SeriesEntity, 'category'> | undefined,
): EventPricingInfo => ({
    markets: eventMarkets.map((m) => ({ ticker: m.ticker, lastPriceCents: m.lastPrice, volume: m.volume })),
    mutuallyExclusive: event.mutuallyExclusive,
    isSportsCategory: series?.category === 'Sports',
});

/**
 * Mirrors `BinaryMarket.inverseMarketTicker`.
 *
 * Short-circuited on market count before doing any work: `resolveInverseTicker` returns null above
 * two markets anyway (events with more than two markets aren't price-exhaustive), so the guard is
 * behaviour-preserving and skips `buildEventPricingInfo`'s whole-array map. Every caller pays that
 * map otherwise, and most call this inside a loop over an event's markets — or once per card per
 * render — so on a large event it is O(markets²) allocations for a result fixed at `undefined`.
 */
export const marketInverseTicker = (
    market: MarketEntity,
    eventMarkets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    series: Pick<SeriesEntity, 'category'> | undefined,
): string | undefined => {
    if (eventMarkets.length > 2) return undefined;
    return resolveInverseTicker(market.ticker, buildEventPricingInfo(eventMarkets, event, series)) ?? undefined;
};

/** Mirrors `BinaryMarket.normalizedLastValidYesPrice`. */
export const marketNormalizedLastValidYesPrice = (
    market: MarketEntity,
    eventMarkets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    series: Pick<SeriesEntity, 'category'> | undefined,
): number => {
    const inverseTicker = marketInverseTicker(market, eventMarkets, event, series);
    if (!inverseTicker) return marketLastValidYesPrice(market);

    const inverseMarket = eventMarkets.find((m) => m.ticker === inverseTicker);
    return 100 - (inverseMarket ? marketLastValidYesPrice(inverseMarket) : 0);
};

/** Mirrors `BinaryMarket.normalizedLastYesPrice`. */
export const marketNormalizedLastYesPrice = (
    market: MarketEntity,
    eventMarkets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    series: Pick<SeriesEntity, 'category'> | undefined,
): number => {
    // A card's market carries live quotes, while eventMarkets stays REST-owned when there is no
    // inverse. Reading our own price from that array would freeze Chance and its delta between polls.
    if (!marketInverseTicker(market, eventMarkets, event, series)) return market.lastPrice;
    return normalizedLastYesPriceCents(market.ticker, buildEventPricingInfo(eventMarkets, event, series));
};

/** Mirrors `BinaryMarket.probability`. */
export const marketProbability = (
    market: MarketEntity,
    eventMarkets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    series: Pick<SeriesEntity, 'category'> | undefined,
): number => {
    if (market.result === 'yes') return 100;
    if (market.result === 'no') return 0;
    return marketNormalizedLastValidYesPrice(market, eventMarkets, event, series);
};

/** Mirrors `BinaryMarket.safeProbability`. */
export const marketSafeProbability = (
    market: MarketEntity,
    eventMarkets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    series: Pick<SeriesEntity, 'category'> | undefined,
): number | undefined => {
    if (market.result === 'yes') return 100;
    if (market.result === 'no') return 0;
    return getSafeProbability(
        marketNormalizedLastYesPrice(market, eventMarkets, event, series),
        market.yesBid,
        market.yesAsk,
    );
};

/** Mirrors `BinaryMarket.normalizedPreviousPeriodYesPrice`. */
export const marketNormalizedPreviousPeriodYesPrice = (
    market: MarketEntity,
    eventMarkets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    series: Pick<SeriesEntity, 'category'> | undefined,
): number => {
    const inverseTicker = marketInverseTicker(market, eventMarkets, event, series);
    if (!inverseTicker) return market.previousPeriodPrice;

    const inverseMarket = eventMarkets.find((m) => m.ticker === inverseTicker);
    return 100 - (inverseMarket?.previousPeriodPrice ?? 0);
};

/** Mirrors `BinaryMarket.yesPriceDelta`. */
export const marketYesPriceDelta = (
    market: MarketEntity,
    eventMarkets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    series: Pick<SeriesEntity, 'category'> | undefined,
): number => {
    const normalizedPreviousPeriodYesPrice = marketNormalizedPreviousPeriodYesPrice(
        market,
        eventMarkets,
        event,
        series,
    );
    const delta = marketNormalizedLastYesPrice(market, eventMarkets, event, series) - normalizedPreviousPeriodYesPrice;
    return normalizedPreviousPeriodYesPrice === 0 ? 0 : delta;
};

/** Mirrors `BinaryMarket.noPriceDelta`. */
export const marketNoPriceDelta = (
    market: MarketEntity,
    eventMarkets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    series: Pick<SeriesEntity, 'category'> | undefined,
): number => -marketYesPriceDelta(market, eventMarkets, event, series);

/** Mirrors `BinaryMarket.absPriceDelta`. */
export const marketAbsPriceDelta = (
    market: MarketEntity,
    eventMarkets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    series: Pick<SeriesEntity, 'category'> | undefined,
): number => Math.abs(marketYesPriceDelta(market, eventMarkets, event, series));

/** Mirrors `BinaryMarket.lastTradedYesPriceInCentsAsString`. */
export const marketLastTradedYesPriceInCentsAsString = (market: MarketEntity): string =>
    market.lastPrice === 0 ? '--¢' : asCents(market.lastPrice);

/** Mirrors `BinaryMarket.lastTradedPriceString`. */
export const marketLastTradedPriceString = (
    market: MarketEntity,
    eventMarkets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    series: Pick<SeriesEntity, 'category'> | undefined,
): string => {
    const probability = marketProbability(market, eventMarkets, event, series);
    const safeProbability = marketSafeProbability(market, eventMarkets, event, series);
    const edgeCaseLessThanOne = !safeProbability && probability < 3 && market.yesBid === 0;
    const sp = safeProbability ? safeProbability : edgeCaseLessThanOne ? '<1' : '--';
    if (typeof sp === 'number') {
        const rounded = parseFloat(sp.toFixed(1));
        return (rounded === 0 ? '<1' : rounded) + '%';
    }
    return sp + '%';
};

/** Mirrors `BinaryMarket.colorPalette`. */
export const marketColorPalette = (market: Pick<MarketEntity, 'rulebookVariables'>): string | null =>
    market.rulebookVariables[colorsPaletteKey] ?? null;

const STRUCTURED_TARGET_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Resolved colors of the market's structured target (including server-side player/team
 * inheritance) — the fallback for markets carrying none of their own, e.g. World Cup countries.
 * Reads React Query's cache directly: the given `queryClient` (the request's own during SSR),
 * else `getCachedStructuredTargetById`'s browser singleton.
 */
export const marketStructuredTargetColors = (
    market: Pick<MarketEntity, 'customStrike'>,
    queryClient?: QueryClient,
): ColorsPalette | undefined => {
    const targetId = Object.values(market.customStrike ?? {}).find(
        (value): value is string => typeof value === 'string' && STRUCTURED_TARGET_UUID.test(value),
    );
    return getCachedStructuredTargetById(targetId, queryClient)?.colors;
};

const CHART_SUBTITLE_TEAM_KEYS = [
    'football_team',
    'basketball_team',
    'baseball_team',
    'hockey_team',
    'soccer_team',
    'cricket_team',
    'rugby_team',
    'lacrosse_team',
] as const;

const CHART_SUBTITLE_COMPETITOR_KEYS = [
    'ufc_competitor',
    'tennis_competitor',
    'tennis_doubles_competitor',
    'golf_competitor',
    'racing_competitor',
    'esports_competitor',
    'olympics_competitor',
    'table_tennis_competitor',
    'boxing_competitor',
    'football_player',
    'basketball_player',
    'baseball_player',
    'hockey_player',
    'cricket_player',
    'rugby_player',
    'lacrosse_player',
] as const;

/**
 * Mirrors `BinaryMarket.yesSubtitleForChart` — resolves a team abbreviation (or competitor
 * last/short name/abbreviation) from the market's `customStrike` via the structured-targets RQ
 * cache, falling back to `yesSubtitle`. Pass the request-scoped client on SSR paths;
 * other consumers default to the browser singleton.
 */
export const marketYesSubtitleForChartWithClient = (
    market: Pick<MarketEntity, 'customStrike' | 'yesSubtitle'>,
    queryClient?: QueryClient,
): string => {
    if (market.customStrike) {
        for (const key of CHART_SUBTITLE_TEAM_KEYS) {
            const teamId = market.customStrike[key];
            if (teamId) {
                const abbreviation = getCachedStructuredTargetById(teamId, queryClient)?.details?.abbreviation;
                if (abbreviation) return abbreviation;
            }
        }
        for (const key of CHART_SUBTITLE_COMPETITOR_KEYS) {
            const competitorId = market.customStrike[key];
            if (competitorId) {
                const details = getCachedStructuredTargetById(competitorId, queryClient)?.details;
                if (details?.last_name) return details.last_name;
                if (details?.short_name) return details.short_name;
                if (details?.abbreviation) return details.abbreviation;
            }
        }
    }
    return market.yesSubtitle;
};

// Keep this unary: callers also pass it directly to Array.map.
export const marketYesSubtitleForChart = (market: Pick<MarketEntity, 'customStrike' | 'yesSubtitle'>): string =>
    marketYesSubtitleForChartWithClient(market);

/**
 * Mirrors `BinaryMarket.chartOverrideColor` — hardcoded chart-line color overrides for a long
 * tail of election-style markets (ticker/subtitle pattern matches), falling back to the market's
 * (or its structured target's) rulebook color palette.
 */
export const marketChartOverrideColor = (
    market: Pick<MarketEntity, 'ticker' | 'yesSubtitle' | 'rulebookVariables' | 'customStrike' | 'colors'>,
    isDarkMode: boolean,
    queryClient?: QueryClient,
): string | undefined => {
    const ticker = market.ticker;

    const balanceOfPowerColor = getBalanceOfPowerColorKey(ticker, market.yesSubtitle);
    if (balanceOfPowerColor) {
        return Palette[balanceOfPowerColor].x10;
    }

    if (ticker === 'KXMAYORNYCPARTY-25-AC') {
        return Palette.Orange.x10;
    } else if (ticker === 'KXMAYORNYCPARTY-25-D') {
        return Palette.Blue.x10;
    } else if (ticker === 'KXMAYORNYCPARTY-25-R') {
        return Palette.Fill.x10;
    } else if (ticker === 'KXMAYORNYCPARTY-25-EADA') {
        return Palette.Fill.x10;
    }

    if (ticker === 'KXATTYGENVA-25-R') {
        return Palette.Red.x10;
    } else if (ticker === 'KXATTYGENVA-25-D') {
        return Palette.Blue.x10;
    }

    if (ticker === 'KXLTGOVVA-25-D') {
        return Palette.Blue.x10;
    } else if (ticker === 'KXLTGOVVA-25-R') {
        return Palette.Red.x10;
    }

    if (ticker.includes('KXMAYORNYCNOMD-25-AC')) {
        return Palette.Brand.Primary;
    } else if (ticker.includes('KXMAYORNYCNOMD-25-ZM')) {
        return Palette.Blue.x10;
    }

    if (ticker.includes('KXNYCMAYORDSECOND-25-AC')) {
        return Palette.Brand.Primary;
    } else if (ticker.includes('KXNYCMAYORDSECOND-25-ZM')) {
        return Palette.Blue.x10;
    }

    if (ticker.includes('KXNYCBOROUGHWINMAN-25-AC')) {
        return Palette.Brand.Primary;
    } else if (ticker.includes('KXNYCBOROUGHWINMAN-25-ZM')) {
        return Palette.Blue.x10;
    }

    if (ticker.includes('KXNYCBOROUGHWINQUEE-25-AC')) {
        return Palette.Brand.Primary;
    } else if (ticker.includes('KXNYCBOROUGHWINQUEE-25-ZM')) {
        return Palette.Blue.x10;
    }

    if (ticker.includes('KXNYCBOROUGHWINBRON-25-AC')) {
        return Palette.Brand.Primary;
    } else if (ticker.includes('KXNYCBOROUGHWINBRON-25-ZM')) {
        return Palette.Blue.x10;
    }

    if (ticker.includes('KXNYCBOROUGHWINSTAT-25-AC')) {
        return Palette.Brand.Primary;
    } else if (ticker.includes('KXNYCBOROUGHWINSTAT-25-ZM')) {
        return Palette.Blue.x10;
    }

    if (ticker.includes('KXNYCBOROUGHWINBROOK-25-AC')) {
        return Palette.Brand.Primary;
    } else if (ticker.includes('KXNYCBOROUGHWINBROOK-25-ZM')) {
        return Palette.Blue.x10;
    }

    if (ticker.includes('KXNYCMAYOR2ND-25-AC')) {
        return Palette.Brand.Primary;
    } else if (ticker.includes('KXNYCMAYOR2ND-25-ZM')) {
        return Palette.Blue.x10;
    }

    if (ticker.includes('SENATE')) {
        if (ticker.includes('MOV')) return undefined;
        if (/-D$/.test(ticker)) return Palette.Blue.x10;
        if (/-R$/.test(ticker)) return Palette.Red.x10;
    }

    if (ticker.includes('PRESPARTY') && !ticker.includes('PRESPARTYFULL')) {
        if (ticker.includes('MOV')) return undefined;
        if (/-D$/.test(ticker)) return Palette.Blue.x10;
        if (/-R$/.test(ticker)) return Palette.Red.x10;
    }

    if (ticker.includes('POPVOTE')) {
        if (ticker.includes('MOV')) return undefined;
        if (/-D$/.test(ticker)) return Palette.Blue.x10;
        if (/-R$/.test(ticker)) return Palette.Red.x10;
    }

    if (ticker.includes('CONTROL')) {
        if (/-D$/.test(ticker)) return Palette.Blue.x10;
        if (/-R$/.test(ticker)) return Palette.Red.x10;
    }

    if (ticker.includes('GOVPARTY')) {
        if (ticker.includes('MOV')) return undefined;
        if (/-D$/.test(ticker)) return Palette.Blue.x10;
        if (/-R$/.test(ticker)) return Palette.Red.x10;
    }

    if (ticker.includes('HOUSE')) {
        if (ticker.includes('MOV')) return undefined;
        if (/-D$/.test(ticker)) return Palette.Blue.x10;
        if (/-R$/.test(ticker)) return Palette.Red.x10;
    }

    if (ticker.includes('PRES')) {
        if (ticker.includes('MOV')) return undefined;
        if (ticker.includes('-DJT')) return Palette.Red.x10;
        if (ticker.includes('-RFK')) return Palette.Purple.x10;
    }

    const colorPalette = marketColorPalette(market);
    const palette = market.colors;
    if (palette) {
        // TODO: More elegant override
        if (colorPalette?.includes('-TIE')) return Palette.Fill.x20;
        return getColorSchemaFromPalette(palette, isDarkMode).primaryRgba;
    }

    // When the market carries no rulebook "Color Palette", fall back to the team
    // color on its structured target (e.g. World Cup countries store it there).
    const structuredTargetPalette = marketStructuredTargetColors(market, queryClient);
    if (structuredTargetPalette) {
        return getColorSchemaFromPalette(structuredTargetPalette, isDarkMode).primaryRgba || undefined;
    }

    return undefined;
};

/** Mirrors `BinaryMarket.chartOverrideColorHue`. */
export const marketChartOverrideColorHue = (
    market: Pick<MarketEntity, 'ticker' | 'yesSubtitle' | 'rulebookVariables' | 'customStrike' | 'colors'>,
): TopLevelHue | undefined => {
    const overrideColorLightMode = marketChartOverrideColor(market, false);
    if (!!overrideColorLightMode) {
        if (overrideColorLightMode.includes('red')) return 'Red' as TopLevelHue;
        if (overrideColorLightMode.includes('blue')) return 'Blue' as TopLevelHue;
        if (overrideColorLightMode.includes('purple')) return 'Purple' as TopLevelHue;
    }
    return undefined;
};

/** Mirrors `BinaryMarket.imageUrl`. */
export const marketImageUrl = (market: MarketEntity): string | null => {
    const imagePath = market.rulebookVariables[imageUrlKey];
    if (!imagePath) return null;
    return toCdnImageUrl(imagePath, 'xs');
};

/** Keys `BinaryMarket.trueRulebookVariables` strips out as internal metadata. */
const TRUE_RULEBOOK_VARIABLES_DROPPED_KEYS = [
    'contract_ticker',
    'expected_determination_time',
    'settlement_timer_hours',
    'early_close_condition',
    'image_link',
    'Multivariate Event Ticker',
    'Color Palette',
];

/** Mirrors `BinaryMarket.trueRulebookVariables`. */
export const marketTrueRulebookVariables = (market: MarketEntity): Record<string, string> =>
    Object.fromEntries(
        Object.entries(market.rulebookVariables).filter(([key]) => !TRUE_RULEBOOK_VARIABLES_DROPPED_KEYS.includes(key)),
    );

/** Shared `|| VARIABLE ||` substitution logic behind `BinaryMarket.underlying`/`.settleDetails`. */
const substituteRulebookVariables = (raw: string, rulebookVariables: Record<string, string>): string => {
    let result = raw.trim();
    for (const variable in rulebookVariables) {
        result = result.replace(
            new RegExp(`\\|\\| *${escapeRegExp(variable)} *\\|\\|`, 'g'),
            rulebookVariables[variable],
        );
    }
    return result;
};

/** Mirrors `BinaryMarket.underlying`. */
export const marketUnderlying = (market: MarketEntity, event: Pick<EventEntity, 'rawUnderlying'>): string =>
    substituteRulebookVariables(event.rawUnderlying, market.rulebookVariables);

/** Mirrors `BinaryMarket.settleDetails`. */
export const marketSettleDetails = (market: MarketEntity, event: Pick<EventEntity, 'rawSettleDetails'>): string =>
    substituteRulebookVariables(event.rawSettleDetails, market.rulebookVariables);

/** Prefer the current series owner; retain the market snapshot as a legacy fallback. */
export const marketContractTermUrl = (market: MarketEntity, owningContractTicker?: string): string => {
    const contractTicker = owningContractTicker?.trim() || market.rulebookVariables[contractTickerKey]?.trim();
    return contractTicker
        ? `${config.kalshiPublicDocsCloudfrontUrl}/contract_terms/${encodeURIComponent(contractTicker)}.pdf`
        : `${config.baseUrl}/regulatory/rulebook`;
};

/** Mirrors `BinaryMarket.americanOdds`. */
export const marketAmericanOdds = (
    market: Pick<MarketEntity, 'yesBid' | 'yesAsk' | 'noBid' | 'noAsk'>,
    isYes: boolean,
    isSell: boolean,
    feeParams: EffectiveFeeParams,
): string => {
    const askWithoutFees = isYes ? (isSell ? market.yesBid : market.yesAsk) : isSell ? market.noBid : market.noAsk;
    return priceToAmericanOdds(askWithoutFees, feeParams.fee_multiplier, feeParams.fee_type, undefined, 0);
};

/** Mirrors `BinaryMarket.parsedFeeWaiverExpirationDate`. */
export const marketParsedFeeWaiverExpirationDate = (market: MarketEntity): DateTime | null =>
    market.feeWaiverExpirationDate === null
        ? null
        : DateTime.fromISO(market.feeWaiverExpirationDate).setZone('America/New_York');

/** Mirrors `BinaryMarket.hasFeeWaiverExpired`. */
export const marketHasFeeWaiverExpired = (market: MarketEntity): boolean =>
    isFeeWaiverExpired(market.feeWaiverExpirationDate);

/** Parses a date string that may be ISO 8601 or a Unix timestamp in seconds (from WS lifecycle). Mirrors `BinaryMarket`'s local `parseDate`. */
const parseMarketDateTime = (value: string): DateTime => {
    const fromISO = DateTime.fromISO(value, { zone: 'America/New_York' });
    if (fromISO.isValid) return fromISO;
    const seconds = Number(value);
    if (!Number.isNaN(seconds)) return DateTime.fromSeconds(seconds, { zone: 'utc' }).setZone('America/New_York');
    return fromISO;
};

/** Mirrors `BinaryMarket.parsedOpenDate`. */
export const marketParsedOpenDate = (market: MarketEntity): DateTime => parseMarketDateTime(market.openDate);

/** Mirrors `BinaryMarket.parsedCloseDate`. */
export const marketParsedCloseDate = (market: MarketEntity): DateTime => parseMarketDateTime(market.closeDate);

/** Mirrors `BinaryMarket.parsedLatestExpirationDate`. */
export const marketParsedLatestExpirationDate = (market: MarketEntity): DateTime =>
    DateTime.fromISO(market.expirationDate).setZone('America/New_York');

/** Mirrors `BinaryMarket.parsedExpectedExpirationDate`. */
export const marketParsedExpectedExpirationDate = (market: MarketEntity): DateTime => {
    if (!market.expectedExpirationDate) return marketParsedLatestExpirationDate(market);

    const expectedDeterminationTime = DateTime.fromISO(market.expectedExpirationDate).setZone('America/New_York');
    if (!expectedDeterminationTime.isValid) return marketParsedLatestExpirationDate(market);

    const latest = marketParsedLatestExpirationDate(market);
    return latest >= expectedDeterminationTime ? expectedDeterminationTime : latest;
};

/** Mirrors `BinaryMarket.numericSettlementTimerSeconds`. */
export const marketNumericSettlementTimerSeconds = (market: MarketEntity): number =>
    Number.isNaN(market.settlementTimerSeconds) ? 7200 : market.settlementTimerSeconds;

/** Mirrors `BinaryMarket.parsedExpectedSettlementDate`. */
export const marketParsedExpectedSettlementDate = (market: MarketEntity): DateTime => {
    const parsedExpectedExpirationDate = marketParsedExpectedExpirationDate(market);
    const parsedCloseDate = marketParsedCloseDate(market);

    const effectiveDate =
        parsedCloseDate.isValid && parsedCloseDate > parsedExpectedExpirationDate
            ? parsedCloseDate
            : parsedExpectedExpirationDate;

    return effectiveDate.plus({ seconds: marketNumericSettlementTimerSeconds(market) });
};

/* -------------------------------------------------------------------------- */
/*                MARKET/EVENT: numeric-forecast statistics                   */
/* -------------------------------------------------------------------------- */

/** Mirrors `BinaryMarket.numericTarget`. */
export const marketNumericTarget = (market: MarketEntity): NumericTarget | null =>
    computeNumericTarget({
        subtitle: market.subtitle,
        strikeType: market.strikeType,
        rulebookVariables: market.rulebookVariables,
        floorStrike: market.floorStrike,
        capStrike: market.capStrike,
    });

/**
 * Mirrors `BinaryMarket.controversy`. Both original branches short-circuited to `0`
 * on the same `isDetermined` check — hoisted here since it's the same condition either way.
 */
export const marketControversy = (market: MarketEntity, event: Pick<EventEntity, 'mutuallyExclusive'>): number => {
    if (isMarketDetermined(market)) return 0;

    if (event.mutuallyExclusive) {
        return market.yesBid - 0.01 * (market.yesAsk - market.yesBid) - 0.01 * Math.abs(100 - market.lastPrice);
    }

    return (
        Math.min(100 - market.yesAsk, market.yesBid) -
        0.01 * (market.yesAsk - market.yesBid) +
        0.01 * Math.abs(50 - market.lastPrice) -
        (market.yesBid === 0 ? 50 : 0) -
        (market.yesAsk === 100 ? 50 : 0)
    );
};

/**
 * Mirrors `Event.isParsableNumeric`. `markets` must already be the event's visible
 * markets — unlike the original getter, this does not re-apply an `isHidden` filter (the
 * content-moderation/admin-hidden-ticker mechanism it depends on has no plain-data
 * equivalent yet; no migrated consumer needs it).
 */
export const eventIsParsableNumeric = (markets: MarketEntity[]): boolean => {
    if (markets.length <= 1) return false;
    if (!markets.every((m) => marketNumericTarget(m) !== null)) return false;

    const lowerBounds = new Set(markets.map((m) => marketNumericTarget(m)?.lowerBound ?? null));
    const upperBounds = new Set(markets.map((m) => marketNumericTarget(m)?.upperBound ?? null));
    if (lowerBounds.size === 1 && upperBounds.size === 1) return false;

    return true;
};

/** Mirrors `Event.numericallySortedMarkets`, except that a 0 bound counts as a bound rather than a missing one. */
export const eventNumericallySortedMarkets = (markets: MarketEntity[]): MarketEntity[] =>
    markets.slice().sort((a, b) => {
        const firstTarget = marketNumericTarget(a);
        const secondTarget = marketNumericTarget(b);
        if (firstTarget?.lowerBound == null && secondTarget?.lowerBound != null) return -1;
        if (firstTarget?.upperBound == null && secondTarget?.upperBound != null) return 1;
        if (firstTarget?.lowerBound != null && secondTarget?.lowerBound != null)
            return firstTarget.lowerBound - secondTarget.lowerBound;
        return 0;
    });

/** Mirrors `Event.isAllGreaterThan`. */
export const eventIsAllGreaterThan = (markets: MarketEntity[]): boolean =>
    eventIsParsableNumeric(markets) &&
    markets.map((m) => marketNumericTarget(m)).every((t) => t?.lowerBound !== null && t?.upperBound === null);

/** Mirrors `Event.isAllLessThan`. */
export const eventIsAllLessThan = (markets: MarketEntity[]): boolean =>
    eventIsParsableNumeric(markets) &&
    markets.map((m) => marketNumericTarget(m)).every((t) => t?.lowerBound === null && t?.upperBound !== null);

/** What `eventBucketsFromBidsAndAsks` reads from the markets. It stays fixed while the quotes change. */
interface NumericLadder {
    numericTargets: NumericTarget[];
    isAllGreaterThan: boolean;
    isAllLessThan: boolean;
}

const numericLadder = (sortedMarkets: MarketEntity[]): NumericLadder => ({
    numericTargets: sortedMarkets.map((m) => marketNumericTarget(m) as NumericTarget),
    isAllGreaterThan: eventIsAllGreaterThan(sortedMarkets),
    isAllLessThan: eventIsAllLessThan(sortedMarkets),
});

const bucketsFromLadder = (
    { numericTargets, isAllGreaterThan, isAllLessThan }: NumericLadder,
    bids: number[],
    asks: number[],
): Bucket[] => {
    const buckets: Bucket[] = [];

    if (isAllGreaterThan) {
        bids = bids.map((bid, i) => max(bids.slice(i)) ?? bid);
        asks = asks.map((ask, i) => min(asks.slice(0, i + 1)) ?? ask);
    } else if (isAllLessThan) {
        bids = bids.map((bid, i) => max(bids.slice(0, i + 1)) ?? bid);
        asks = asks.map((ask, i) => min(asks.slice(i)) ?? ask);
    }

    const marketProbabilities = bids.map((b, i) => (b + asks[i]) / 200);

    for (let i = 0; i < numericTargets.length; i++) {
        if (bids[i] === 0) continue;
        if (asks[i] === 100 && bids[i] < 97) continue;

        const target = numericTargets[i];
        const adjustedTarget = { lowerBound: target.lowerBound, upperBound: target.upperBound };
        let midpoint = 0;
        let probability = 0;
        if (target.lowerBound === null && target.upperBound !== null) {
            if (i === 0) {
                midpoint = target.upperBound;
                probability = marketProbabilities[i];
            } else {
                adjustedTarget.lowerBound = numericTargets[i - 1].upperBound;
                midpoint = (target.upperBound + (numericTargets[i - 1].upperBound ?? 0)) / 2;
                probability = marketProbabilities[i] - marketProbabilities[i - 1];
            }
        } else if (target.lowerBound !== null && target.upperBound !== null) {
            midpoint = (target.lowerBound + target.upperBound) / 2;
            probability = marketProbabilities[i];
        } else if (target.lowerBound !== null && target.upperBound === null) {
            if (i === numericTargets.length - 1) {
                midpoint = target.lowerBound;
                probability = marketProbabilities[i];
            } else {
                adjustedTarget.upperBound = numericTargets[i + 1].lowerBound;
                midpoint = (target.lowerBound + (numericTargets[i + 1].lowerBound ?? 0)) / 2;
                probability = marketProbabilities[i] - marketProbabilities[i + 1];
            }
        }

        probability = Math.max(0, probability);
        buckets.push({ midpoint, probability, adjustedTarget });

        if (
            isAllGreaterThan &&
            i === numericTargets.length - 1 &&
            buckets.map((b) => b.probability).reduce((partialSum, a) => partialSum + a, 0) < 1
        ) {
            let lowerBoundOfImpliedBucket =
                numericTargets.length > 1
                    ? (numericTargets[0].lowerBound ?? 0) -
                      ((numericTargets[1].lowerBound ?? 0) - (numericTargets[0].lowerBound ?? 0))
                    : (numericTargets[0].lowerBound ?? 0);

            if (numericTargets[0].lowerBound === 0) {
                lowerBoundOfImpliedBucket = 0;
            }

            const impliedTarget = {
                lowerBound: lowerBoundOfImpliedBucket,
                upperBound: numericTargets[0].lowerBound ?? 0,
            };
            buckets.unshift({
                midpoint: (impliedTarget.upperBound + impliedTarget.lowerBound) / 2,
                probability: 1 - buckets.map((b) => b.probability).reduce((partialSum, a) => partialSum + a, 0),
                adjustedTarget: impliedTarget,
            });
        }
    }

    return buckets;
};

/** Mirrors `Event.bucketsFromBidsAndAsks`. */
export const eventBucketsFromBidsAndAsks = (markets: MarketEntity[], bids: number[], asks: number[]): Bucket[] =>
    bucketsFromLadder(numericLadder(eventNumericallySortedMarkets(markets)), bids, asks);

/** Mirrors `Event.probabilityBuckets` at the current (non-historical) market state — no `pastDate` support. */
export const eventProbabilityBuckets = (markets: MarketEntity[]): Bucket[] => {
    if (!eventIsParsableNumeric(markets)) return [];

    const sortedMarkets = eventNumericallySortedMarkets(markets);
    const bids = sortedMarkets.map((m) => m.yesBid);
    const asks = sortedMarkets.map((m) => m.yesAsk);
    return eventBucketsFromBidsAndAsks(markets, bids, asks);
};

/** Mirrors `Event.mean` at the current (non-historical) market state — no `pastDate` support. */
export const eventMean = (markets: MarketEntity[]): number => {
    const buckets = eventProbabilityBuckets(markets);
    const naiveEV = buckets.map((b) => b.midpoint * b.probability).reduce((partialSum, a) => partialSum + a, 0);
    return naiveEV / buckets.map((b) => b.probability).reduce((partialSum, a) => partialSum + a, 0.00001);
};

/** Mirrors `Event.featuredSubmarkets`. */
export const eventFeaturedSubmarkets = (
    markets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    count: number,
    openFirst: boolean,
): MarketEntity[] => {
    let sorted = markets.slice().sort((a, b) => marketControversy(b, event) - marketControversy(a, event));
    sorted = sorted.sort((a, b) => (isMarketOpen(a) === isMarketOpen(b) ? 0 : isMarketOpen(b) === openFirst ? 1 : -1));
    return sorted.slice(0, count);
};

export interface EventMostControversialSubmarketsOptions {
    ifWithPositions?: boolean;
    ifOpenFirst?: boolean;
}

/**
 * Mirrors `Event.mostControversialSubmarkets`. `markets` should already be `eventMarkets(...)`'s
 * output. `marketIdsWithActivePosition` should come from `useMarketIdsWithActivePosition`
 * (`queries/eventPositions.ts`) — keyed by market `id` rather than the original getter's `.ticker`
 * lookup on `traderStore.portfolio.positions`, since `id` is the semantically correct key
 * (matches `PositionVo.market_id`) and this port has no reason to reproduce that inconsistency.
 */
export const eventMostControversialSubmarkets = (
    markets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    count: number,
    marketIdsWithActivePosition: Set<string>,
    { ifWithPositions = false, ifOpenFirst = true }: EventMostControversialSubmarketsOptions = {},
): MarketEntity[] => {
    const isDetermined = eventIsDetermined(markets);

    const mostControversial = isDetermined
        ? markets
              .slice()
              .reverse()
              .sort((a, b) => b.volume - a.volume)
              .sort((a, b) => marketControversy(b, event) - marketControversy(a, event))
              .sort((a, b) => (marketResultIsYes(a) === marketResultIsYes(b) ? 0 : marketResultIsYes(b) ? 1 : -1))
              .slice(0, count)
        : markets
              .slice()
              .sort((a, b) => b.volume - a.volume)
              .sort((a, b) => marketControversy(b, event) - marketControversy(a, event))
              .sort((a, b) => (isMarketOpen(a) === isMarketOpen(b) ? 0 : isMarketOpen(b) === ifOpenFirst ? 1 : -1))
              .slice(0, count);

    const otherMarketsWithPositions = markets.filter(
        (m) => marketIdsWithActivePosition.has(m.id) && !mostControversial.includes(m),
    );

    return ifWithPositions ? mostControversial.concat(otherMarketsWithPositions) : mostControversial;
};

export interface EventMostControversialChunkOptions {
    /**
     * Keeps a numeric ladder centered on a previously chosen market while live
     * quotes change. Falls back to the current most-controversial market when
     * the ticker is no longer present.
     */
    anchorTicker?: string;
    ifWithPositions?: boolean;
}

/** Mirrors `Event.mostControversialChunk`. `markets` should already be `eventMarkets(...)`'s output. */
export const eventMostControversialChunk = (
    markets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    count: number,
    marketIdsWithActivePosition: Set<string>,
    { anchorTicker, ifWithPositions = false }: EventMostControversialChunkOptions = {},
): MarketEntity[] => {
    const positionIndices = markets
        .map((m, index) => (marketIdsWithActivePosition.has(m.id) ? index : -1))
        .filter((index) => index >= 0);
    const firstPositionIndex = min(positionIndices) ?? null;
    const lastPositionIndex = max(positionIndices) ?? null;

    let firstIndex = 0;
    let lastIndex = 0;

    const anchorIndex = anchorTicker ? markets.findIndex((market) => market.ticker === anchorTicker) : -1;
    const most =
        anchorIndex >= 0 ? undefined : eventMostControversialSubmarkets(markets, event, 1, marketIdsWithActivePosition);
    if (anchorIndex < 0 && !most?.length) return [];

    const loc = anchorIndex >= 0 ? anchorIndex : markets.findIndex((market) => market.ticker === most?.[0]?.ticker);
    if (loc < 0) return [];

    if (loc - Math.trunc(count / 2) < 0) {
        firstIndex = 0;
        lastIndex = count;
    } else if (loc + Math.ceil(count / 2) >= markets.length) {
        firstIndex = markets.length - count;
        lastIndex = markets.length;
    } else {
        firstIndex = loc - Math.trunc(count / 2);
        lastIndex = loc + Math.ceil(count / 2);
    }

    return markets.slice(
        ifWithPositions ? (min([firstIndex, firstPositionIndex ?? lastIndex]) ?? firstIndex) : firstIndex,
        ifWithPositions ? (max([lastIndex, (lastPositionIndex ?? 0) + 1]) ?? lastIndex) : lastIndex,
    );
};

export interface EventShrunkMarketsOptions {
    /** Fixed center for numeric-ladder windowing; ignored by non-numeric events. */
    numericWindowAnchorTicker?: string;
}

/**
 * Mirrors `Event.shrunkMarkets`. `markets` should already be `eventMarkets(...)`'s output.
 * `marketIdsWithActivePosition` should come from `useMarketIdsWithActivePosition`.
 */
export const eventShrunkMarkets = (
    markets: MarketEntity[],
    event: Pick<EventEntity, 'mutuallyExclusive'>,
    marketIdsWithActivePosition: Set<string>,
    shrunkSize = 3,
    sorted = false,
    hideDetermined?: boolean,
    { numericWindowAnchorTicker }: EventShrunkMarketsOptions = {},
): MarketEntity[] => {
    const isParsableNumeric = eventIsParsableNumeric(markets);

    let shrunkMarkets: MarketEntity[];
    if (isParsableNumeric) {
        shrunkMarkets = eventMostControversialChunk(markets, event, shrunkSize, marketIdsWithActivePosition, {
            anchorTicker: numericWindowAnchorTicker,
            ifWithPositions: true,
        });
    } else {
        const mostControversialIds = eventMostControversialSubmarkets(
            markets,
            event,
            shrunkSize,
            marketIdsWithActivePosition,
            { ifWithPositions: true, ifOpenFirst: true },
        ).map((m) => m.id);
        shrunkMarkets = markets.filter((m) => mostControversialIds.includes(m.id));
        if (sorted) {
            shrunkMarkets = shrunkMarkets
                .slice()
                .sort((a, b) => mostControversialIds.indexOf(a.id) - mostControversialIds.indexOf(b.id));
        }
    }

    let returnedMarkets = markets;
    const shouldOptionallyShrinkMarkets = shrunkMarkets.length <= markets.length - 2;
    if (shouldOptionallyShrinkMarkets || sorted) {
        returnedMarkets = shrunkMarkets;
    }

    if (hideDetermined && !isParsableNumeric) {
        const allDetermined = returnedMarkets.every(isMarketDetermined);
        const allDisplaying = shrunkSize >= returnedMarkets.length;
        const isInitialLoad = shrunkSize === 3;
        if (!allDetermined && !allDisplaying && isInitialLoad) {
            returnedMarkets = returnedMarkets.filter((m) => !isMarketDetermined(m));
        }
    }

    return returnedMarkets;
};

/** Mirrors `Event.hasManyMarkets`. `markets` should already be `eventMarkets(...)`'s output. */
export const eventHasManyMarkets = (markets: MarketEntity[]): boolean => markets.length > 15;

/**
 * Mirrors `Event.singleOpenDate`. NOTE: faithfully preserves the original's spread-into-Set
 * quirk (`new Set(...arr)` passes only the first element to the Set constructor, so this compares
 * the characters of the first market's `openDate` string, not the set of distinct open dates) —
 * it is therefore ~always `false` for real ISO-date data. `chartMarketDisplay`'s only use
 * (`getChartMarketsToLoad`) effectively falls through to the `isClosed` branch. Ported as-is to
 * keep behavior identical rather than "fixing" a getter no code relies on being true.
 */
export const eventSingleOpenDate = (markets: MarketEntity[]): boolean =>
    new Set(...markets.map((m) => m.openDate)).size === 1;

/**
 * The shared "top markets to chart" selection used by `EventPriceHistoriesGraph`/
 * `chartMarketDisplay`, the Hub grids, and `CalendarOrderPanel`. Entity variant: `markets`/`series`
 * in place of `event.*` getters, with the position input (`event.shrunkMarkets`' `ifWithPositions`)
 * passed explicitly as `marketIdsWithActivePosition` (from `useMarketIdsWithActivePosition`) since
 * this is a pure function with no direct store access. `highlightedMarkets` must be refs drawn from
 * `markets` (their order is resolved via `markets.indexOf`, matching the live contract).
 */
export const eventBaseTopMarkets = (
    markets: MarketEntity[],
    event: Pick<EventEntity, 'ticker' | 'mutuallyExclusive'>,
    series: Pick<SeriesEntity, 'category'> | undefined,
    marketIdsWithActivePosition: Set<string>,
    highlightedMarkets?: MarketEntity[],
    prioritizedTickers: string[] = [],
): MarketEntity[] => {
    if (event.ticker === 'KXBALANCEPOWERCOMBO-27FEB') {
        return markets;
    }
    if (highlightedMarkets) {
        // Bound chart history query fan-out even when callers bypass the widget param parser.
        const selectedHighlightedMarkets = highlightedMarkets
            .filter((m) => m.lastPrice > 0)
            .sort((a, b) => markets.indexOf(a) - markets.indexOf(b))
            .slice(0, MAX_HIGHLIGHTED_MARKETS);
        // A prioritized strike still displaces a highlighted market rather than widening the
        // selection, so the cap stays the caller's own count until it exceeds the chart maximum.
        return applyPrioritizedStrikes(
            selectedHighlightedMarkets,
            markets,
            prioritizedTickers,
            Math.min(highlightedMarkets.length, MAX_HIGHLIGHTED_MARKETS),
        );
    }

    const isSports = isSeriesSports(series);
    const isSportWithTie = isSports && markets.length === 3 && markets.some(isTieMarket);

    let marketsToSelect = eventShrunkMarkets(markets, event, marketIdsWithActivePosition, markets.length, true);
    if (isSportWithTie) {
        marketsToSelect = marketsToSelect.filter((m) => !isTieMarket(m));
    }

    const maxMarkets = isSports ? 2 : 3;
    const filteredMarkets = marketsToSelect
        .slice(0, maxMarkets)
        .filter((m) => m.lastPrice > 0)
        .sort((a, b) => markets.indexOf(a) - markets.indexOf(b));

    if (prioritizedTickers.length > 0) {
        return applyPrioritizedStrikes(filteredMarkets, markets, prioritizedTickers, maxMarkets);
    }

    return filteredMarkets;
};

/**
 * Mirrors `Event.mainlineMarkets` — this event's markets sorted by closeness to a 50¢
 * coin-flip (the spread/total "mainline" ordering for the American-odds row), open markets
 * first. Bypasses `eventFeaturedSubmarkets`'s controversy score deliberately, same as the
 * original getter (that score ranks the heaviest mutually-exclusive favorite first, the wrong
 * order here). `markets` should already be `eventMarkets(...)`'s output (mirrors `this.markets`).
 */
export const eventMainlineMarkets = (markets: MarketEntity[]): MarketEntity[] => {
    const score = (m: MarketEntity): number => {
        if (m.yesBid === 0 || m.yesAsk === 100) return Number.NEGATIVE_INFINITY;
        return Math.min(100 - m.yesAsk, m.yesBid) - 0.01 * (m.yesAsk - m.yesBid);
    };

    return markets
        .slice()
        .sort((a, b) => score(b) - score(a))
        .sort((a, b) => (isMarketOpen(a) === isMarketOpen(b) ? 0 : isMarketOpen(b) ? 1 : -1));
};

/** Mirrors `Event.mainlineMarket`. */
export const eventMainlineMarket = (markets: MarketEntity[]): MarketEntity | undefined =>
    eventMainlineMarkets(markets)[0];

/**
 * Mirrors `Event.isTrueBinary`. `markets` should already be `eventMarkets(...)`'s output
 * (mirrors `this.markets`).
 */
export const eventIsTrueBinary = (markets: MarketEntity[]): boolean =>
    markets.length === 1 && marketNumericTarget(markets[0]) === null;

/**
 * Mirrors `Event.expirationValue`. `markets` should already be `eventMarkets(...)`'s output
 * (mirrors `this.markets`).
 */
export const eventExpirationValue = (markets: MarketEntity[]): string =>
    markets.map((m) => m.expirationValue).find((ev) => ev !== '') || '';

const normalizedRulebookSeparatorTypeKeys = new Set(separatorTypeKeys.map((key) => key.toLowerCase()));

/**
 * Mirrors `BinaryMarket.rulebookSeparatorType` — reads the market's rulebook variables for a
 * "how does this market's numeric range relate to its strike" separator (`between`/`above`/`below`),
 * used by financial-index/knockout events to pick which sibling market covers the live underlying
 * value. NOT the same concept as `MarketEntity.floorStrike`/`.capStrike` (those mirror the raw API
 * `floor_strike`/`cap_strike` fields, unrelated to this rulebook-variable-derived range logic) —
 * hence the `marketRulebook*` naming here to keep the two concepts visibly distinct.
 */
export const marketRulebookSeparatorType = (market: MarketEntity): string =>
    Object.entries(market.rulebookVariables).find(([key]) =>
        normalizedRulebookSeparatorTypeKeys.has(key.toLowerCase()),
    )?.[1] ?? '';

/** Mirrors `BinaryMarket.countKeyValue`. */
export const marketRulebookCountKeyValue = (market: MarketEntity): number => {
    const count = market.rulebookVariables[countKey] ? parseFloat(market.rulebookVariables[countKey]) : undefined;
    return count && !isNaN(count) ? count : 0;
};

/** Mirrors `BinaryMarket.capStrike` (the rulebook-derived getter, not the `MarketEntity.capStrike` API field). */
export const marketRulebookCapStrike = (market: MarketEntity): number | undefined => {
    const cap = market.rulebookVariables[capStrikeKey] ? parseFloat(market.rulebookVariables[capStrikeKey]) : undefined;
    switch (marketRulebookSeparatorType(market)) {
        case 'between':
            return cap && !isNaN(cap) ? cap : undefined;
        case 'above':
            return Infinity;
        case 'below':
            return marketRulebookCountKeyValue(market);
        default:
            return undefined;
    }
};

/** Mirrors `BinaryMarket.floorStrike` (the rulebook-derived getter, not the `MarketEntity.floorStrike` API field). */
export const marketRulebookFloorStrike = (market: MarketEntity): number | undefined => {
    const floor = market.rulebookVariables[floorStrikeKey]
        ? parseFloat(market.rulebookVariables[floorStrikeKey])
        : undefined;
    switch (marketRulebookSeparatorType(market)) {
        case 'between':
            return floor && !isNaN(floor) ? floor : undefined;
        case 'below':
            return -Infinity;
        case 'above':
            return marketRulebookCountKeyValue(market);
        default:
            return undefined;
    }
};

/** Mirrors `BinaryMarket.isOnRange`. */
export const marketIsOnRange = (market: MarketEntity, financialUnderlyingValue: number): boolean => {
    const cap = marketRulebookCapStrike(market);
    const floor = marketRulebookFloorStrike(market);
    if (!cap || !floor || !financialUnderlyingValue) return false;
    return financialUnderlyingValue >= floor && financialUnderlyingValue <= cap;
};

/**
 * Mirrors `BinaryMarket.isOnRangeWithMaxCount`/`.isOnRangeWithMinCount`. `siblingMarkets` should be
 * the full set of markets in `market`'s parent event (mirrors `this.parentEvent.markets`).
 */
export const marketIsOnRangeWithMaxCount = (
    market: MarketEntity,
    siblingMarkets: MarketEntity[],
    financialUnderlyingValue: number,
): boolean => {
    const marketsOnRange = siblingMarkets.filter((m) => marketIsOnRange(m, financialUnderlyingValue));
    const marketWithMaxCount = marketsOnRange.reduce(
        (acc, m) => (marketRulebookCountKeyValue(m) > marketRulebookCountKeyValue(acc) ? m : acc),
        market,
    );
    return marketIsOnRange(market, financialUnderlyingValue) && market.ticker === marketWithMaxCount.ticker;
};

export const marketIsOnRangeWithMinCount = (
    market: MarketEntity,
    siblingMarkets: MarketEntity[],
    financialUnderlyingValue: number,
): boolean => {
    const marketsOnRange = siblingMarkets.filter((m) => marketIsOnRange(m, financialUnderlyingValue));
    const marketWithMinCount = marketsOnRange.reduce(
        (acc, m) => (marketRulebookCountKeyValue(m) < marketRulebookCountKeyValue(acc) ? m : acc),
        market,
    );
    return marketIsOnRange(market, financialUnderlyingValue) && market.ticker === marketWithMinCount.ticker;
};

/**
 * Mirrors `BinaryMarket.isOnTheBestRange` — is `market` the sibling that best covers
 * `financialUnderlyingValue`, per its rulebook separator type.
 */
export const marketIsOnTheBestRange = (
    market: MarketEntity,
    siblingMarkets: MarketEntity[],
    financialUnderlyingValue: number,
): boolean => {
    switch (marketRulebookSeparatorType(market)) {
        case 'between':
            return marketIsOnRange(market, financialUnderlyingValue);
        case 'below':
            return marketIsOnRangeWithMinCount(market, siblingMarkets, financialUnderlyingValue);
        case 'above':
            return marketIsOnRangeWithMaxCount(market, siblingMarkets, financialUnderlyingValue);
        default:
            return false;
    }
};

/**
 * `event.title` is the raw `EventEntity.title` (~= `Event.formattedTitle`), not the
 * `**`-stripped `Event.title` getter — stripped inline here to match that getter's contract,
 * even though the only use (a "hurricane" substring check) wouldn't practically differ either way.
 */
export const eventMarketForm = (
    event: Pick<EventEntity, 'seriesTicker' | 'title'>,
    markets: MarketEntity[],
): MarketForm => {
    if (isFinancialUnderlyingSeries(event.seriesTicker)) return MarketForm.FINANCIAL_UNDERLYING;

    const strippedTitle = event.title.replace(/\*\*/g, '');
    const showNumeric = eventIsParsableNumeric(markets) && !strippedTitle.toLowerCase().includes('hurricane');
    if (showNumeric) return MarketForm.NUMERICAL;

    if (markets.length < 2) return MarketForm.BINARY;

    return MarketForm.CATEGORICAL;
};

/**
 * Mirrors `Event.title` — `**`-stripped, unlike raw `EventEntity.title` (`~= Event.formattedTitle`,
 * see `eventMarketForm`'s doc comment above for the same gotcha). Use this whenever a raw
 * `event.title` read needs to be replaced by an entity read (e.g. share text, analytics).
 */
export const eventTitle = (event: Pick<EventEntity, 'title'>): string => event.title.replace(/\*\*/g, '');

/** Mirrors `Event.titleWithoutHour`. */
export const eventTitleWithoutHour = (
    event: Pick<EventEntity, 'title'>,
    series: Pick<SeriesEntity, 'frequency'> | undefined,
): string => {
    if (series?.frequency !== 'hourly') return event.title;
    if (!event.title.includes(' at ')) return event.title;
    return event.title.split(' at ')[0];
};

/** Mirrors `Event.subtitleHour`. */
export const eventSubtitleHour = (
    event: Pick<EventEntity, 'subTitle'>,
    series: Pick<SeriesEntity, 'frequency'> | undefined,
): string | undefined => {
    if (series?.frequency !== 'hourly') return undefined;
    if (!event.subTitle.includes(' at ')) return undefined;
    return event.subTitle.split(' at ')[1];
};

/** Mirrors `Event.subTitleStartsWithPreposition`. */
export const eventSubTitleStartsWithPreposition = (event: Pick<EventEntity, 'subTitle'>): boolean => {
    const splitSubtitle = event.subTitle.split(' ');
    if (splitSubtitle.length < 1) return false;
    return prepositions.has(splitSubtitle[0].toLowerCase());
};

/**
 * `event.title` is stripped of `**` markers here to match the original `Event.title` getter, since
 * `EventEntity.title` is the raw, unstripped VO field.
 */
export const marketTitleSubtitle = (market: MarketEntity | undefined, event: EventEntity | undefined): string =>
    market && event ? `${event.title.replace(/\*\*/g, '')}${market.subtitle ? ` : ${market.subtitle}` : ''}` : '';

/** Mirrors `BinaryMarket.joinedSubtitle`. */
export const marketJoinedSubtitle = (market: MarketEntity, event: Pick<EventEntity, 'subTitle'>): string => {
    const subTitle = event.subTitle;
    const startsWithPreposition = eventSubTitleStartsWithPreposition(event);
    const eventSubtitle = startsWithPreposition ? lowerFirstLetter(subTitle) : subTitle;
    const marketSubtitle = market.subtitle;

    if (!eventSubtitle) return marketSubtitle;
    if (!marketSubtitle) return eventSubtitle;

    return startsWithPreposition ? `${marketSubtitle} ${eventSubtitle}` : `${marketSubtitle}, ${eventSubtitle}`;
};

/** Mirrors `BinaryMarket.noSubtitle`. */
export const marketNoSubtitle = (market: Pick<MarketEntity, 'yesSubtitle'>): string =>
    market.yesSubtitle ? `Not ${lowerFirstLetter(market.yesSubtitle)}` : '';

/* -------------------------------------------------------------------------- */
/*        EVENT: smoothed historical means (candlestick-backed fallback)      */
/* -------------------------------------------------------------------------- */

/** Mirrors `BinaryMarket.mapCandlesticksToStatsHistory` — the pre-extrapolation shape `eventSmoothedHistoricalMeans` needs. */
export { emptyStatsHistory, mapCandlesticksToStatsHistory } from 'utils/candlestickHistoryTransform';

/** Mirrors `BinaryMarket.mapCandlesticksToCandlesticksStatsHistory` — the OHLC shape `sampledHistory('candlesticks')` needs. */
export const mapCandlesticksToCandlesticksStatsHistory = (
    candlesticks: MarketCandlestickVo[],
    periodInterval: HistoricalDataPeriodInterval,
): HistoricalMarketCandlestickStats[] => {
    let accumulatedVolume = 0;
    let lastNonZeroPriceCandlestick = candlesticks[0];
    const periodIntervalInSeconds = periodIntervalToSeconds(periodInterval);

    return candlesticks.map((candlestick) => {
        accumulatedVolume += Math.floor(candlestick.volume / 2);
        if (candlestick.price.close > 0) {
            lastNonZeroPriceCandlestick = candlestick;
        }

        return {
            yesAsk: lastNonZeroPriceCandlestick.yes_ask,
            yesBid: lastNonZeroPriceCandlestick.yes_bid,
            price: lastNonZeroPriceCandlestick.price,
            volume: accumulatedVolume,
            openInterest: Math.floor(candlestick.open_interest / 2),
            startTs: candlestick.end_period_ts - periodIntervalInSeconds,
            ts: candlestick.end_period_ts - periodIntervalInSeconds,
        };
    });
};

/**
 * Mirrors `BinaryMarket.extrapolateStatsHistory` — fills timestamp gaps in `statsHistory` by
 * repeating the prior point at each missing `periodInterval` boundary. Only the STATS (line)
 * ladder is extrapolated this way; `candlestickStatsHistory` is used as-is (gap-filling for
 * candlesticks happens later, in `boundedSampledHistory`'s doji-repeat sampling step).
 */
export const extrapolateStatsHistory = (
    statsHistory: HistoricalMarketStats[],
    periodInterval: HistoricalDataPeriodInterval,
): HistoricalMarketStats[] => {
    if (statsHistory.length === 0) return statsHistory;

    const extrapolatedArray: HistoricalMarketStats[] = [];
    const periodIntervalInSeconds = periodIntervalToSeconds(periodInterval);
    const lastTs = statsHistory[statsHistory.length - 1].ts;

    let currentTs = statsHistory[0].ts;
    let currentIndex = 0;

    while (currentTs <= lastTs) {
        const currentItem = statsHistory[currentIndex];
        extrapolatedArray.push(currentItem);
        currentTs = currentItem.ts;

        if (currentIndex === statsHistory.length - 1) {
            break;
        }
        const nextItem = statsHistory[currentIndex + 1];

        while (nextItem && nextItem.ts > currentTs + periodIntervalInSeconds) {
            extrapolatedArray.push({ ...currentItem, ts: currentTs + periodIntervalInSeconds });
            currentTs += periodIntervalInSeconds;
        }

        currentIndex++;
    }

    return extrapolatedArray;
};

const INITIAL_STATS: HistoricalMarketStats = {
    yesAsk: 50,
    yesBid: 50,
    price: 0,
    volume: 0,
    openInterest: 0,
    ts: 0,
};
/**
 * Mirrors `BinaryMarket`'s module-level `INITIAL_CANDLESTICK_STATS` — including its
 * `startTs`/`ts` quirk: `boundedSampledHistory`'s `updateSamplingFromDataPoint` never
 * overwrites `startTs` on the scratch "sampling" object, so any synthesized/doji-repeated
 * point produced before the FIRST real data point in the window keeps whatever `startTs`
 * this constant was built with — frozen at module-load time in the original class (a
 * `Date.now()` computed once and reused forever), never read by any chart consumer.
 * Hardcoded to `0` here rather than `Date.now()` for determinism — same harmless-garbage
 * behavior, just a fixed placeholder instead of a frozen wall-clock moment.
 */
const INITIAL_CANDLESTICK_STATS: HistoricalMarketCandlestickStats = {
    yesAsk: { open: 50, close: 50, high: 50, low: 50, mean: 50 },
    yesBid: { open: 50, close: 50, high: 50, low: 50, mean: 50 },
    price: { open: 0, close: 0, high: 0, low: 0, mean: 0 },
    volume: 0,
    openInterest: 0,
    startTs: 0,
    ts: 0,
};

/** Mirrors `BinaryMarket.emptyStatsHistory`/`emptyCandlestickStatsHistory` — the placeholder a market's history defaults to before its first fetch resolves. Never truly empty (`boundedSampledHistory` assumes a first element exists). */
export const emptyCandlestickStatsHistory = (
    now: number = Math.round(Date.now() / 1000),
): HistoricalMarketCandlestickStats[] => [{ ...INITIAL_CANDLESTICK_STATS, startTs: now, ts: now }];

/**
 * Mirrors `BinaryMarket.boundedSampledHistory` — resamples raw history into `sampleSize` evenly
 * spaced points across `[start, end]`, repeating the last known point (as a flat doji for
 * candlesticks) to fill any gap where the underlying data polls less often than the sample rate.
 */
export function boundedSampledHistory(
    data: HistoricalMarketStats[],
    start: number,
    end: number,
    sampleSize: number,
    type?: 'stats',
    hideOffHours?: boolean,
    backfillFirstPrice?: boolean,
): HistoricalMarketStats[];
export function boundedSampledHistory(
    data: HistoricalMarketCandlestickStats[],
    start: number,
    end: number,
    sampleSize: number,
    type: 'candlesticks',
    hideOffHours?: boolean,
    backfillFirstPrice?: boolean,
): HistoricalMarketCandlestickStats[];
export function boundedSampledHistory(
    data: HistoricalMarketStats[] | HistoricalMarketCandlestickStats[],
    start: number,
    end: number,
    sampleSize: number,
    type: MarketStatsType = 'stats',
    // Accepted for signature parity with `BinaryMarket.boundedSampledHistory` — unused there too.
    _hideOffHours = false,
    backfillFirstPrice = false,
): HistoricalMarketStats[] | HistoricalMarketCandlestickStats[] {
    const pointIsCandlestick = (
        point: HistoricalMarketStats | HistoricalMarketCandlestickStats,
    ): point is HistoricalMarketCandlestickStats => !!point && type === 'candlesticks';
    const repeatCandleStickAsDoji = (
        candlestick: HistoricalMarketCandlestickStats,
    ): HistoricalMarketCandlestickStats => {
        const repeatedCandlestick = JSON.parse(JSON.stringify(candlestick)) as HistoricalMarketCandlestickStats;
        const { price, yesAsk, yesBid } = candlestick;
        repeatedCandlestick.price = { ...price, open: price.close, high: price.close, low: price.close };
        repeatedCandlestick.yesAsk = { ...yesAsk, open: yesAsk.close, high: yesAsk.close, low: yesAsk.close };
        repeatedCandlestick.yesBid = { ...yesBid, open: yesBid.close, high: yesBid.close, low: yesBid.close };
        return repeatedCandlestick;
    };

    const updateSamplingFromDataPoint = (
        sampling: HistoricalMarketStats | HistoricalMarketCandlestickStats,
        dataPoint: HistoricalMarketStats | HistoricalMarketCandlestickStats,
    ): void => {
        sampling.yesAsk = dataPoint.yesAsk;
        sampling.yesBid = dataPoint.yesBid;
        sampling.openInterest = dataPoint.openInterest;
        sampling.price = dataPoint.price;
        sampling.volume = dataPoint.volume;
    };

    const windowSize = end - start + 1;
    const samplingRate = windowSize / sampleSize;
    let expandedData: HistoricalMarketStats[] | HistoricalMarketCandlestickStats[] = [];
    const sampling: HistoricalMarketStats | HistoricalMarketCandlestickStats = {
        ...(type === 'stats' ? INITIAL_STATS : INITIAL_CANDLESTICK_STATS),
        ts: start,
    };

    // volume to deduct from sampled points.
    let usedVolume = start >= data[0].ts ? data[0]?.volume : 0;
    for (let i = 0; i < data.length && data[i].ts < end; i++) {
        // if data rate is low, collect multiple samples for single datapoint
        let isFirstSampleForDataPoint = true;
        while (sampling.ts <= data[i].ts) {
            const currentVolume = sampling.volume - usedVolume;

            if (pointIsCandlestick(sampling)) {
                const repeatedCandlestick = isFirstSampleForDataPoint ? sampling : repeatCandleStickAsDoji(sampling);
                (expandedData as HistoricalMarketCandlestickStats[]).push({
                    ...repeatedCandlestick,
                    volume: currentVolume,
                });
            } else {
                (expandedData as HistoricalMarketStats[]).push({ ...sampling, volume: currentVolume });
            }
            sampling.ts += samplingRate;
            usedVolume += currentVolume;
            isFirstSampleForDataPoint = false;
        }
        updateSamplingFromDataPoint(sampling, data[i]);

        if (data[i].ts <= start) {
            usedVolume = data[i].volume;
        }
    }

    if (data.length > 0) {
        updateSamplingFromDataPoint(sampling, data[data.length - 1]);
    }
    // if needed, collect more samples after by repeating last available datapoint
    let isFirstSampleForDataPoint = true;
    while (sampling.ts < end + samplingRate) {
        const currentVolume = sampling.volume - usedVolume;
        if (pointIsCandlestick(sampling)) {
            const repeatedCandlestick = isFirstSampleForDataPoint ? sampling : repeatCandleStickAsDoji(sampling);
            (expandedData as HistoricalMarketCandlestickStats[]).push({
                ...repeatedCandlestick,
                volume: currentVolume,
            });
        } else {
            (expandedData as HistoricalMarketStats[]).push({ ...sampling, volume: currentVolume });
        }
        sampling.ts += samplingRate;
        usedVolume += currentVolume;
        isFirstSampleForDataPoint = false;
    }

    // Filter out points with no price, volume, or open interest
    const filteredData = makeMembersCallable(expandedData).filter((stat) =>
        pointIsCandlestick(stat) ? !!stat.price.open || !!stat.volume : !!stat.price || !!stat.volume,
    );

    // Backfill first price if needed
    if (backfillFirstPrice && filteredData.length > 0 && type === 'stats') {
        const firstPrice = (filteredData[0] as HistoricalMarketStats).price;
        expandedData = makeMembersCallable(expandedData).map((stat) => ({
            ...stat,
            price: (stat as HistoricalMarketStats).price === 0 ? firstPrice : (stat as HistoricalMarketStats).price,
        })) as HistoricalMarketStats[];
    } else {
        expandedData = makeMembersCallable(filteredData) as
            | HistoricalMarketStats[]
            | HistoricalMarketCandlestickStats[];
    }

    return expandedData;
}

/**
 * Mirrors `BinaryMarket.sampledHistory` — wraps `boundedSampledHistory` with the market's own
 * open/close window and appends a settlement data point for determined markets (so the chart
 * ends at the resolution price instead of the last traded price). `now` defaults to the
 * call-time clock (no reactive timer — recompute on your own cadence, same as `isMarketClosed`).
 */
export function marketSampledHistory(
    market: MarketEntity,
    statsHistory: HistoricalMarketStats[],
    candlestickStatsHistory: HistoricalMarketCandlestickStats[],
    period: PERIOD_TYPE,
    type: 'candlesticks',
    setSampleSize?: number,
    now?: number,
): HistoricalMarketCandlestickStats[];
export function marketSampledHistory(
    market: MarketEntity,
    statsHistory: HistoricalMarketStats[],
    candlestickStatsHistory: HistoricalMarketCandlestickStats[],
    period: PERIOD_TYPE,
    type?: 'stats',
    setSampleSize?: number,
    now?: number,
): HistoricalMarketStats[];
export function marketSampledHistory(
    market: MarketEntity,
    statsHistory: HistoricalMarketStats[],
    candlestickStatsHistory: HistoricalMarketCandlestickStats[],
    period: PERIOD_TYPE,
    type: MarketStatsType = 'stats',
    setSampleSize: number | undefined = undefined,
    now: number = Math.round(Date.now() / 1000),
): HistoricalMarketStats[] | HistoricalMarketCandlestickStats[] {
    const marketOpenTs = Math.round(new Date(market.openDate).getTime() / 1000);
    const marketCloseTs = Math.round(new Date(market.closeDate).getTime() / 1000);

    const end = Math.min(marketCloseTs, now);
    const start = calculateStartTs(end, marketOpenTs, period);

    const sampleSize = !!setSampleSize
        ? setSampleSize
        : type === 'candlesticks'
          ? getCandlestickSamplingPointsNumber()
          : getLineSamplingPointsNumber(period);

    const data =
        type === 'candlesticks'
            ? boundedSampledHistory(candlestickStatsHistory, start, end, sampleSize, 'candlesticks')
            : boundedSampledHistory(statsHistory, start, end, sampleSize);

    if (isMarketDetermined(market) && data.length > 0 && !!market.result) {
        const settlementPrice = market.settlementValueCents;
        const lastPoint = data[data.length - 1];
        const settlementTs = lastPoint.ts + 1;

        if (type === 'candlesticks') {
            const ohlc = {
                open: settlementPrice,
                close: settlementPrice,
                high: settlementPrice,
                low: settlementPrice,
                mean: settlementPrice,
            };
            (data as HistoricalMarketCandlestickStats[]).push({
                yesAsk: ohlc,
                yesBid: ohlc,
                price: ohlc,
                volume: 0,
                openInterest: 0,
                startTs: settlementTs,
                ts: settlementTs,
            });
        } else {
            (data as HistoricalMarketStats[]).push({
                yesAsk: settlementPrice,
                yesBid: settlementPrice,
                price: settlementPrice,
                volume: 0,
                openInterest: 0,
                ts: settlementTs,
            });
        }
    }

    return data;
}

/** Mirrors `BinaryMarket.lastBidAskBeforeTime`. */
export const marketLastBidAskBeforeTime = (statsHistory: HistoricalMarketStats[], date: Date): [number, number] => {
    const ts = date.getTime() / 1000;
    if (statsHistory.length === 0 || statsHistory[0].ts > ts) return [0, 100];

    const beforeStats = statsHistory.filter((s) => s.ts < ts);
    const stat = beforeStats[beforeStats.length - 1];
    if (stat === undefined) return [0, 100];

    return [stat.yesBid, stat.yesAsk];
};

/** Each market's last quote strictly before `date`, in the order of `sortedMarkets`. */
const eventBidsAndAsksAtTime = (
    sortedMarkets: MarketEntity[],
    statsHistoryByTicker: Record<string, HistoricalMarketStats[]>,
    date: Date,
): [number[], number[]] => {
    const bids: number[] = [];
    const asks: number[] = [];
    for (const market of sortedMarkets) {
        const [bid, ask] = marketLastBidAskBeforeTime(statsHistoryByTicker[market.ticker] ?? [], date);
        bids.push(bid);
        asks.push(ask);
    }
    return [bids, asks];
};

/** Mirrors `Event.probabilityBuckets(pastDate)` — the historical-lookup path `probabilityBuckets`/`eventProbabilityBuckets` don't cover. */
export const eventProbabilityBucketsAtTime = (
    markets: MarketEntity[],
    statsHistoryByTicker: Record<string, HistoricalMarketStats[]>,
    date: Date,
): Bucket[] => {
    if (!eventIsParsableNumeric(markets)) return [];

    const sortedMarkets = eventNumericallySortedMarkets(markets);
    return bucketsFromLadder(
        numericLadder(sortedMarkets),
        ...eventBidsAndAsksAtTime(sortedMarkets, statsHistoryByTicker, date),
    );
};

/**
 * Builds the `[bucket count, forecast]` estimate `eventSmoothedHistoricalMeans` takes at each sample and at the
 * live point. It is built once per history from `eventNumericallySortedMarkets` output, and every sample's quotes
 * arrive in that order. A zero count marks the sample illiquid, so the previous forecast carries forward.
 */
export type NumericForecastEstimator = (
    sortedMarkets: MarketEntity[],
) => (bids: number[], asks: number[]) => [number, number];

/** Mirrors `Event.bucketCountAndMean`, the bucket-weighted mean. `eventSmoothedHistoricalMeans`' default estimator. */
export const eventBucketCountAndMean: NumericForecastEstimator = (sortedMarkets) => {
    const ladder = numericLadder(sortedMarkets);
    return (bids, asks) => {
        const buckets = bucketsFromLadder(ladder, bids, asks);
        const naiveEV = buckets.map((b) => b.midpoint * b.probability).reduce((partialSum, a) => partialSum + a, 0);
        return [
            buckets.length,
            naiveEV / buckets.map((b) => b.probability).reduce((partialSum, a) => partialSum + a, 0.00001),
        ];
    };
};

/**
 * Mirrors `Event.openDate` — the earliest market's open date. `setZone: true` preserves the
 * offset the backend sent (always UTC) instead of converting to the runtime's local zone, so
 * the returned string is deterministic across environments.
 */
export const eventOpenDate = (markets: MarketEntity[]): string => {
    const dates = markets.map((m) => DateTime.fromISO(m.openDate, { setZone: true })).sort((a, b) => (a > b ? 1 : -1));
    return dates.length > 0 ? (dates[0].toISO() ?? '') : '';
};

/**
 * Mirrors `Event.closeDate` for a loaded event — skips the `!loaded` short-circuit (an
 * `EventEntity` only exists once loaded, same rationale as `eventIsClosed`) and the
 * `fixNflDate` one-off correction for a narrow 2025/2027 `KXNFLGAME*` data bug, which is
 * irrelevant outside that specific series.
 */
export const eventCloseDate = (markets: MarketEntity[]): string => {
    if (markets.length === 0) return '';
    const dates = markets.map((m) => DateTime.fromISO(m.closeDate, { setZone: true })).sort((a, b) => (a > b ? 1 : -1));
    return dates.slice(-1)[0]?.toISO() ?? '';
};

/**
 * `eventCloseDate`, but with the removed `Event.fixNflDate` correction re-applied — for
 * `2025`-dated `KXNFLGAME*` events whose close date comes back from the backend two years ahead
 * (`2027` instead of `2025`), rewrites it back rather than requesting/rendering a two-year-long
 * chart range. https://kalshi.slack.com/archives/CSHRA7JLD/p1757605510543909
 *
 * `eventCloseDate` deliberately skips this (see its own doc comment) since it's irrelevant
 * outside this one series — use that for every other consumer. This variant exists solely for
 * `MarketPriceHistoryGraphWrapper`'s forecast-history range, which mirrors the removed
 * `BinaryMarket.loadMarketForecastHistory`'s use of `parentEvent.closeDate` (the getter that
 * included the fix).
 */
export const eventCloseDateForForecastHistory = (
    event: Pick<EventEntity, 'ticker' | 'targetDatetime'>,
    markets: MarketEntity[],
): string => {
    const closeDate = eventCloseDate(markets);
    if (
        !closeDate ||
        !event.ticker.startsWith('KXNFLGAME') ||
        !event.targetDatetime.startsWith('2025') ||
        !closeDate.startsWith('2027')
    ) {
        return closeDate;
    }
    return DateTime.fromISO(closeDate.replace('2027', '2025')).plus({ hours: 12 }).toISO() ?? closeDate;
};

/**
 * Mirrors `Event.nextCloseDate` — the earliest close date among `markets` (ascending, take
 * first). `setZone: true` preserves the backend's UTC offset instead of converting to the
 * runtime's local zone, so the returned string is deterministic across environments.
 */
export const eventNextCloseDate = (markets: MarketEntity[]): string => {
    if (markets.length === 0) return '';
    const dates = markets.map((m) => DateTime.fromISO(m.closeDate, { setZone: true })).sort((a, b) => (a > b ? 1 : -1));
    return dates[0]?.toISO() ?? '';
};

/** Mirrors `Event.minimalSubTitle`. */
export const eventMinimalSubTitle = (event: Pick<EventEntity, 'subTitle'>): string =>
    getMinimalSubtitle(event.subTitle);

/** Mirrors `Event.minimumSubtitleWithoutHour`. */
export const eventMinimumSubtitleWithoutHour = (
    event: Pick<EventEntity, 'subTitle'>,
    series: Pick<SeriesEntity, 'frequency'> | undefined,
): string => {
    const minimalSubTitle = eventMinimalSubTitle(event);
    if (series?.frequency !== 'hourly') return minimalSubTitle;
    if (!event.subTitle.includes(' at ')) return minimalSubTitle;
    return minimalSubTitle.split(' at ')[0];
};

/**
 * Mirrors `Event.pending`. The original compares against `this.statusClockMs` (a field
 * resynced periodically to avoid an uncached `Date.now()` read in a computed getter) — irrelevant
 * here since a plain function has no computed-value caching to protect, so `now` is just the
 * call-time clock (matching `isMarketClosed`/`eventIsClosed`'s convention).
 */
export const eventPending = (markets: MarketEntity[], now: DateTime = DateTime.now()): boolean => {
    const openDate = eventOpenDate(markets);
    if (!openDate) return false;
    return DateTime.fromISO(openDate) >= now;
};

/** Mirrors `Event.isUnopened`. */
export const eventIsUnopened = (
    event: EventEntity,
    markets: MarketEntity[],
    now: DateTime = DateTime.now(),
): boolean => {
    if (eventIsClosed(event, markets, now) || eventHasOpenMarkets(markets)) return false;
    return markets.some((m) => m.status === 'initialized');
};

/** Mirrors `Event.isClosingSoon`. */
export const eventIsClosingSoon = (
    event: EventEntity,
    markets: MarketEntity[],
    now: DateTime = DateTime.now(),
): boolean => getIsClosingSoon(eventCloseDate(markets), eventIsClosed(event, markets, now), event.ticker);

/**
 * Mirrors `Event.smoothedHistoricalMeans`. `allCandlesticksLoaded` replaces the original getter's
 * `this.markets.some((m) => !m.isCandlesticksLoaded)` guard — the caller (a React Query hook,
 * not a store loading flag) knows whether every market's candlestick fetch has resolved.
 * `estimator` replaces the bucket-weighted mean at every sample and the live point; margin
 * forecasts use it to plot the market median instead.
 */
export const eventSmoothedHistoricalMeans = (
    markets: MarketEntity[],
    statsHistoryByTicker: Record<string, HistoricalMarketStats[]>,
    allCandlesticksLoaded: boolean,
    numPoints = 200,
    estimator: NumericForecastEstimator = eventBucketCountAndMean,
): [Date[], number[]] => {
    if (!eventIsParsableNumeric(markets) || !allCandlesticksLoaded) return [[], []];

    // Accumulate min/max in one pass — spreading into Math.min/Math.max overflows the call
    // stack when the stats history has hundreds of thousands of entries.
    let minStatHistoryTime = Infinity;
    let maxStatHistoryTime = -Infinity;
    for (const market of markets) {
        for (const stat of statsHistoryByTicker[market.ticker] ?? []) {
            if (stat.ts < minStatHistoryTime) minStatHistoryTime = stat.ts;
            if (stat.ts > maxStatHistoryTime) maxStatHistoryTime = stat.ts;
        }
    }
    if (minStatHistoryTime === Infinity) return [[], []];

    const startTs = new Date(Math.max(new Date(eventOpenDate(markets)).getTime(), minStatHistoryTime * 1000 + 1000));
    const endTs = new Date(maxStatHistoryTime * 1000);

    // Cut out the first 20% to avoid awkward price discovery
    const laterStartTs = new Date((startTs.getTime() * 4 + endTs.getTime()) / 5);

    const allTimes = linspace(laterStartTs.getTime(), endTs.getTime(), numPoints).map((num) => new Date(num));

    const sortedMarkets = eventNumericallySortedMarkets(markets);
    const estimate = estimator(sortedMarkets);
    const allBucketCountsAndMeans = allTimes.map((pastDate): [number, number] =>
        estimate(...eventBidsAndAsksAtTime(sortedMarkets, statsHistoryByTicker, pastDate)),
    );

    // Pass liquid measurements forward in one pass
    for (let i = 1; i < allBucketCountsAndMeans.length; i++) {
        if (allBucketCountsAndMeans[i][0] === 0) {
            allBucketCountsAndMeans[i] = allBucketCountsAndMeans[i - 1];
        }
    }

    const liquidTimes: Date[] = [];
    const liquidBucketCountsAndMeans: [number, number][] = [];
    // Clear illiquid measurements from the start
    for (let i = 1; i < allBucketCountsAndMeans.length; i++) {
        if (allBucketCountsAndMeans[i][0] !== 0) {
            liquidBucketCountsAndMeans.push(allBucketCountsAndMeans[i]);
            liquidTimes.push(allTimes[i]);
        }
    }

    // Exit early if there's no data
    if (liquidBucketCountsAndMeans.length === 0) return [[], []];

    const adjustedTimes: Date[] = [];
    const means: number[] = [];

    // Merge adjacent entries that have different bucket acounts
    let ifShouldSkip = false;
    for (let i = 0; i < liquidTimes.length; i++) {
        if (ifShouldSkip) {
            ifShouldSkip = false;
            continue;
        }

        if (i === liquidTimes.length - 1 || liquidBucketCountsAndMeans[i][0] === liquidBucketCountsAndMeans[i + 1][0]) {
            adjustedTimes.push(liquidTimes[i]);
            means.push(liquidBucketCountsAndMeans[i][1]);
        } else {
            adjustedTimes.push(new Date((liquidTimes[i].getTime() + liquidTimes[i + 1].getTime()) / 2));
            means.push((liquidBucketCountsAndMeans[i][1] + liquidBucketCountsAndMeans[i + 1][1]) / 2);
        }
    }

    const currentTime = new Date();
    if (eventHasOpenMarkets(markets)) {
        const [bucketCount, current] = estimate(
            sortedMarkets.map((m) => m.yesBid),
            sortedMarkets.map((m) => m.yesAsk),
        );

        if (bucketCount !== 0) {
            means.push(current);
            adjustedTimes.push(currentTime);
        } else if (means.length > 0) {
            means.push(means.slice(-1)[0]);
            adjustedTimes.push(currentTime);
        }
    }

    return [adjustedTimes, means];
};
